import React, { useState, useEffect, useRef } from "react";
import { ref, onValue } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { database, firestore, functions } from '../../firebaseConfig';
import { doc, deleteDoc, updateDoc, getDoc, deleteField } from 'firebase/firestore';
import { addFooterToEmail, convertToHtml } from '../EmailFooterGenerator';
import { resolveFooterForSend } from './resolveFooterForSend';
import ScreenComposerPopup from '../ScreenComposerPopup';
import ScreenComposerNotifier from '../ScreenComposerNotifier';
import useScreenComposerBackground from '../useScreenComposerBackground';
import BulkModeProgress from './BulkModeComponents/BulkModeProgress';
import BulkGenerationLoading from './BulkModeComponents/BulkGenerationLoading';
import EmailHistoryPanel from './EmailHistoryPanel';
import useSentRecipients from './useSentRecipients';
import useUndeliverableEmails from './useUndeliverableEmails';
import FollowUpHistoryWarning from './FollowUpHistoryWarning';
import CallContextChip from './CallContextChip';
import LiveSpamBadge from './LiveSpamBadge';
import useSpamCheckMode from './useSpamCheckMode';
import { requestAiSpamCheck } from './requestAiSpamCheck';
import { isFollowUpTopicType } from './previousEmailContext';
import { duplicateFollowUpSends } from './duplicateFollowUpSend';
import ConfirmModal from '../ConfirmModal';
import SendConfirmModal from './SendConfirmModal';
import ResearchInfoTabs from './ResearchInfoTabs';
import RichEmailEditor from './RichEmailEditor';
import { collectAiImageTokens, generateAiImageForContact, withResolvedAiImages, withResolvedImageAltText, buildImagePromptVariables, isSkippedImageError, promoteImageGeneration, IMAGE_PRIORITY_ACTIVE, IMAGE_PRIORITY_PREFETCH } from './dynamicImageUtils';
import { 
  generateEmailForContact, 
  updateContactStatus,
  saveLastSenderToDatabase 
} from './BulkModeComponents/bulkModeHelpers';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../../utils/cloudFunctions';
import { 
  trackEmailSend,
  selectRandomWithTracking,
  findTopicInUserTopics,
  coupleScoreToAnalytics,
  resolveTemplateForContact,
  resolveFlowEmailStep,
  hasInstructionBlocks,
  stripInstructionBlocks,
} from './emailTrackingUtils';
import { advanceFlowOnEmailSend } from '../warmupFlowUtils';
import { resolveFlowSenderEmail } from '../flowSenderUtils';
import { buildCloneWriterBrief, isVoiceCloneTopicData } from '../CloneTemplate/buildVoiceClonePayload';
import { accountTextSignature } from '../accountTextSignature';
import { pickPreparedSenderAccount, resolvePreparedSendEmail } from '../preparedSendAccount';
import { effectiveContactStatus, topicForPreparedDraft } from './contactStage';
import { useCopyClones } from '../CopyCloneAI/useCopyClones';
import { autoCloneName } from '../CopyCloneAI/cloneUtils';
import useCreditBalance, { estimateBulkCost } from '../../hooks/useCreditBalance';
import useCreditGate from '../../hooks/useCreditGate';
import { emailGenerationCost, costOf } from '../../config/creditPlans';
import { useActiveWorld } from '../../hooks/useActiveWorld';
import useCustomFields from '../../hooks/useCustomFields';
import { getAllContactEmails, getDecisionMakersWithoutEmail, getFirstDmName, getActiveTemplatePref, getActiveDmFallback, getRecipientInfoForContact, resolvePreferredEmail } from '../../shared/enums';
import { resolveFollowUpSubject } from '../../utils/followUpSubject';
import emailPersonalities from '../emailPersonalities';
import { SALESGOD_PERSONAS } from '../../shared/salesgodPersonas';

const WRITER_CATEGORY_ICONS = {
  icebreaker: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v8l4-4"/><path d="M12 10l-4-4"/><path d="M20 16.2A4.5 4.5 0 0017.5 8h-1.8A7 7 0 104 14.9"/><path d="M8 19h8"/><path d="M10 22h4"/></svg>,
  lossframing: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  problemformula: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/><line x1="11" y1="8" x2="11" y2="14"/></svg>,
  proofstage: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
  contentcta: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
};
const WRITER_CATEGORIES = [
  { id: 'icebreaker', label: 'Icebreaker' },
  { id: 'lossframing', label: 'Loss Framing' },
  { id: 'problemformula', label: 'Diagnosis' },
  { id: 'proofstage', label: 'Proof' },
  { id: 'contentcta', label: 'Content CTA' },
];

// Lightweight stable hash of the Writer's input context. When this changes (email body
// or context edited), the cached digest is invalidated so the backend rebuilds it.
const hashWriterContext = (...parts) => {
  const str = parts.filter(Boolean).join('||');
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return String(h);
};

// Helper-Funktion für URL-Formatierung
const formatURL = (url, prefix) => {
  if (!url) return '';
  
  // Wenn URL bereits http:// oder https:// enthält
  if (url.match(/^https?:\/\//i)) {
    return url;
  }
  
  // Entferne @ von Instagram-Handles
  if (prefix === 'https://instagram.com/') {
    url = url.replace('@', '');
  }
  
  return `${prefix}${url}`;
};

const BulkModeEmail = ({ 
  onClose, 
  rowData, 
  userId, 
  isReply, 
  source, 
  onEmailResult,
  userSettings,
  aiSettings,
  userTopics,
  isTopicsLoaded,
  senderEmails,
  topicTypeMapping,
  bulkContacts,
  allData,
  // contactId -> mail parked in SafeGuard (see safeGuardResend.buildResendPreset).
  // Seeded like prepared mails and sent as approved.
  presetEmails = null
}) => {
  const { currentUserId } = useActiveWorld();
  const { balance: creditBalance } = useCreditBalance(userId);
  const { requireCredits, openCreditsModal } = useCreditGate();
  const { contextFields: customContextFields } = useCustomFields(userId);
  const [creditError, setCreditError] = useState(null);
  
  // Grundlegende E-Mail-States
  const [selectedTopic, setSelectedTopic] = useState("Introduce Yourself");
  // Style removed — defined in template
  const [contextInfo, setContextInfo] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const resendConfirmedRef = useRef(false);
  // Follow-up-Sanity-Guard: separater Confirm-Dialog vor dem Send, sobald
  // eine Konversation zu diesem Kontakt existiert (auch bei Follow-up-Steps).
  // Zeigt Subject + Datum der letzten gesendeten Nachricht, damit der User
  // sicher ist, dass er nicht denselben Schritt versehentlich doppelt sendet.
  const [followUpConfirmOpen, setFollowUpConfirmOpen] = useState(false);
  const followUpConfirmedRef = useRef(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPerfecting, setIsPerfecting] = useState(false);
  const [perfectSliderOpen, setPerfectSliderOpen] = useState(false);
  const [perfectCreativity, setPerfectCreativity] = useState(3);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [writingSparks, setWritingSparks] = useState([]);
  const [sparksLoading, setSparksLoading] = useState(false);
  const [sparksLoadingPos, setSparksLoadingPos] = useState(null);
  const [sparksCopied, setSparksCopied] = useState(null);
  const [writerDirections, setWriterDirections] = useState([]);
  const [activeDirection, setActiveDirection] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [sidePanelShowMore, setSidePanelShowMore] = useState(false);
  const [cachedBriefing, setCachedBriefing] = useState(null);
  const [expandingIdx, setExpandingIdx] = useState(null);
  const [sparksGenCount, setSparksGenCount] = useState(0);
  const [activePersona, setActivePersona] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [frameworkPickerOpen, setFrameworkPickerOpen] = useState(false);
  const [expandedPersonality, setExpandedPersonality] = useState(null);
  const [regenCustomNote, setRegenCustomNote] = useState('');
  // Quick CopyTwin Re-Generate popover (lives next to the 3-dots menu).
  const [cloneQuickOpen, setCloneQuickOpen] = useState(false);
  const regenQuickRef = useRef(null);
  // All trained CopyTwins in this workspace — lets a Re-Generate on a
  // CopyTwin template switch the clone for a single contact.
  const { clones: copyClones } = useCopyClones(userId);
  const [ccEmails, setCcEmails] = useState([]);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  const [randomSenderMode, setRandomSenderMode] = useState(false);
  
  // Bulk-Modus-spezifische States
  const [currentBulkIndex, setCurrentBulkIndex] = useState(0);
  const [currentBulkContact, setCurrentBulkContact] = useState(null);
  const [bulkGeneratedEmails, setBulkGeneratedEmails] = useState({});
  const [isBulkGenerating, setIsBulkGenerating] = useState(true);
  const [bulkGenerationProgress, setBulkGenerationProgress] = useState(0);
  const [bulkGenerationComplete, setBulkGenerationComplete] = useState(false);
  const [sentEmails, setSentEmails] = useState({});
  const [showScreenComposer, setShowScreenComposer] = useState(false);
  const [screenComposerImage, setScreenComposerImage] = useState(null);
  // Per-contact dynamic AI images: contactId -> { token -> { status, imageUrl, ... } }
  const [bulkAiImages, setBulkAiImages] = useState({});
  // Pre-prepared flow emails: when contacts were opened from the World DB
  // "Prepared" tab, the backend already generated their emails (subject, body,
  // tracking, AI images). We seed them directly so Bulk mode shows everything
  // instantly with NO live AI generation. `preparedChecked` gates the normal
  // generation trigger until we know whether prepared emails exist.
  const [preparedChecked, setPreparedChecked] = useState(false);
  const preparedContactsRef = useRef(new Set());
  const hasPreparedEmailsRef = useRef(false);
  const preparedLoadStartedRef = useRef(false);
  const preparedSenderSyncedForRef = useRef(null);
  // contactId -> { topicName, emailType } of the seeded prepared draft. The
  // draft's stage is authoritative; guessing it from the contact status is what
  // sent prepared follow-ups out as intros.
  const preparedMetaRef = useRef({});
  // Contacts whose topic the user picked by hand — the prepared topic sync
  // and the prepared email type then step aside for that contact.
  const manualTopicForRef = useRef(new Set());
  // "contactId::token" keys the user cancelled/removed. A late-resolving
  // generation for such a key is ignored so it can never resurrect the image.
  const removedBulkImageTokensRef = useRef(new Set());
  // Set when Bulk mode closes. Prefetched generations still waiting for a slot
  // are then dropped before they start — the only point at which an image still
  // costs nothing, since a running generation always bills.
  const bulkImagesAbortedRef = useRef(false);
  const [subjectAltsOpen, setSubjectAltsOpen] = useState(false);
  const [subjectAlts, setSubjectAlts] = useState([]);
  const [subjectAltsLoading, setSubjectAltsLoading] = useState(false);
  // True when subjectAlts came pre-baked from the last generation for the
  // current bulk contact — first ⚡ click opens the dropdown instantly.
  const [subjectAltsPrecomputed, setSubjectAltsPrecomputed] = useState(false);
  const [checkingDmEmails, setCheckingDmEmails] = useState({});
  const [dmEmailOverrides, setDmEmailOverrides] = useState({});
  const [inlineAddDm, setInlineAddDm] = useState(null);
  const [savingDm, setSavingDm] = useState(false);

  const scBackground = useScreenComposerBackground(userId, (imageData) => {
    setScreenComposerImage(imageData);
  });

  // ── Live spam-risk badge (see NormalModeEmail for the rationale) ──
  // In AI mode, per-contact results are pre-computed in the background during
  // bulk generation and cached on bulkGeneratedEmails[id]._spam, so navigating
  // between contacts shows the verdict instantly.
  const spamCheckMode = useSpamCheckMode(userId);
  const [aiSpam, setAiSpam] = useState(null);
  const [aiSpamPending, setAiSpamPending] = useState(false);
  const aiSpamReqId = useRef(0);
  const aiSpamAnalyzedRef = useRef(null);
  const spamPrecomputeSet = useRef(new Set());

  // Whether the current step is a follow-up. On a follow-up a "Re:/Fwd:" subject
  // prefix is legitimate (reply in an existing thread), not a fake-reply trick,
  // so the spam check must not flag it. Also drives the follow-up history warning.
  const isFollowUpStep = isFollowUpTopicType(topicTypeMapping[selectedTopic]);

  // Background pre-computation: as bulk emails get generated, run the AI spam
  // check for each one and cache it on bulkGeneratedEmails[id]._spam, so the
  // badge is ready the instant the user navigates to that contact.
  useEffect(() => {
    if (spamCheckMode !== 'ai') return;
    Object.entries(bulkGeneratedEmails).forEach(([id, em]) => {
      if (!em || em.error || em._spam || spamPrecomputeSet.current.has(id)) return;
      spamPrecomputeSet.current.add(id);
      const combined = `${em.subject || ''}\u0000${em.body || ''}`;
      requestAiSpamCheck({ subject: em.subject || '', body: em.body || '', isFollowUp: isFollowUpStep, userId }).then((result) => {
        setBulkGeneratedEmails((prev) => {
          const ex = prev[id];
          if (!ex) return prev;
          return { ...prev, [id]: { ...ex, _spam: { _text: combined, result } } };
        });
      }).catch(() => { spamPrecomputeSet.current.delete(id); });
    });
  }, [bulkGeneratedEmails, spamCheckMode, isFollowUpStep, userId]);

  useEffect(() => {
    if (spamCheckMode !== 'ai') { setAiSpam(null); setAiSpamPending(false); return undefined; }
    const hasContent = (bodyContent && bodyContent.trim()) || (subjectLine && subjectLine.trim());
    if (!hasContent) { setAiSpam(null); setAiSpamPending(false); aiSpamAnalyzedRef.current = null; return undefined; }
    const combined = `${subjectLine || ''}\u0000${bodyContent || ''}`;

    const genEmail = currentBulkContact ? bulkGeneratedEmails[currentBulkContact.id] : null;

    // Cache hit from background pre-computation → show instantly, no call.
    const cached = genEmail && !genEmail.error ? genEmail._spam : null;
    if (cached && cached._text === combined) {
      if (aiSpamAnalyzedRef.current !== combined || !aiSpam) {
        aiSpamAnalyzedRef.current = combined;
        setAiSpam(cached.result);
        setAiSpamPending(false);
      }
      return undefined;
    }
    if (aiSpam && aiSpamAnalyzedRef.current === combined) { setAiSpamPending(false); return undefined; }

    // The background sweeper is already analyzing this exact (unedited) email —
    // don't fire a second call, just wait for its cached result to arrive.
    const originalCombined = genEmail && !genEmail.error ? `${genEmail.subject || ''}\u0000${genEmail.body || ''}` : null;
    if (currentBulkContact && originalCombined === combined && spamPrecomputeSet.current.has(currentBulkContact.id)) {
      setAiSpamPending(true);
      return undefined;
    }

    const id = ++aiSpamReqId.current;
    setAiSpamPending(true);
    const timer = setTimeout(async () => {
      const result = await requestAiSpamCheck({ subject: subjectLine, body: bodyContent, isFollowUp: isFollowUpStep, userId });
      if (id !== aiSpamReqId.current) return;
      aiSpamAnalyzedRef.current = combined;
      setAiSpam(result);
      setAiSpamPending(false);
    }, 1400);
    return () => clearTimeout(timer);
  }, [bodyContent, subjectLine, spamCheckMode, aiSpam, currentBulkContact, bulkGeneratedEmails, isFollowUpStep, userId]);

  // Ref für das Betreff-Input-Element
  const subjectInputRef = useRef(null);
  // Ref für die Bulk-Generierung
  const hasStartedBulkGeneration = useRef(false);
  // Ref für paralleles Senden
  const pendingSends = useRef({});
  const sentContactsRef = useRef(new Set());
  const deletingRef = useRef(false);
  const autoAdvanceTimerRef = useRef(null);
  // Ref für das Action-Menu
  const actionMenuRef = useRef(null);
  const expectedContactIdRef = useRef(null);
  const sparksLockRef = useRef(false);
  const expandLockRef = useRef(false);
  // SalesGod Writer digest cache + angle ledger (see hashWriterContext).
  const usedAnglesRef = useRef([]);
  const digestHashRef = useRef(null);

  // State für Session-Statistiken
  const [sessionStats, setSessionStats] = useState({
    sent: 0,
    skipped: 0,
    deleted: 0
  });
  
  // Refs für akkurate Zählung
  const sessionDeletedRef = useRef(0);
  const sessionSentRef = useRef(0);
  const sessionSkippedRef = useRef(0);
  
  // Neuer State für die aktualisierte Liste der Bulk-Kontakte
  const [updatedBulkContacts, setUpdatedBulkContacts] = useState(bulkContacts);
  
  // States um zu verfolgen, ob der Benutzer manuell Änderungen vorgenommen hat
  const [userEditedSubject, setUserEditedSubject] = useState(false);
  const [userEditedBody, setUserEditedBody] = useState(false);
  const [lastContactId, setLastContactId] = useState(null);

  // Active flow definitions (for flow-aware template + sender selection)
  const [warmupFlows, setWarmupFlows] = useState([]);
  const [leadFlows, setLeadFlows] = useState([]);

  // Initialisiere die aktualisierte Kontaktliste
  useEffect(() => {
    setUpdatedBulkContacts(bulkContacts);
  }, [bulkContacts]);

  // Load active warmup + retargeting flows so bulk sending can honour a
  // contact's current "Send Email" flow step (template + sender override).
  useEffect(() => {
    if (!userId) return;
    const toActiveList = (data) => data
      ? Object.keys(data).map((key) => ({ id: key, ...data[key] })).filter(f => f.isActive)
      : [];
    const warmupRef = ref(database, `World/${userId}/settings/warmupFlows`);
    const leadRef = ref(database, `World/${userId}/settings/leadFlows`);
    const unsubWarmup = onValue(warmupRef, (snap) => setWarmupFlows(toActiveList(snap.val())));
    const unsubLead = onValue(leadRef, (snap) => setLeadFlows(toActiveList(snap.val())));
    return () => { unsubWarmup(); unsubLead(); };
  }, [userId]);

  // Bestimmt das passende Topic anhand des Kontakt-Status oder AI Tags
  const determineTopicForContact = async (contact) => {
    if (!contact) return "Introduce Yourself";
    
    console.log('📧 [BULK-MODE] determineTopicForContact for:', contact.business_name || 'Unnamed', contact.id);

    const preparedMeta = preparedMetaRef.current[contact.id];
    const preparedTopic = preparedMeta ? topicForPreparedDraft(preparedMeta, topicTypeMapping) : null;
    if (preparedTopic) return preparedTopic;

    // 0a. Active flow "Send Email" step template wins over everything else.
    const flowStep = resolveFlowEmailStep(contact, warmupFlows, leadFlows);
    if (flowStep?.templateId && userTopics) {
      const flowTemplate = userTopics[flowStep.templateId];
      if (flowTemplate?.topic) {
        console.log('📧 [BULK-MODE] Using flow step template:', flowTemplate.topic, '(template:', flowStep.templateId, ')');
        return flowTemplate.topic;
      }
    }

    // 0b. Per-contact template override has priority (supports per-stage templates)
    const resolvedTplId = resolveTemplateForContact(contact);
    if (resolvedTplId && userTopics) {
      const overrideData = userTopics[resolvedTplId];
      if (overrideData?.topic) {
        console.log('📧 [BULK-MODE] Using per-contact template override:', overrideData.topic, '(template:', resolvedTplId, ')');
        return overrideData.topic;
      }
    }
    
    // 1. Prüfe zuerst, ob der Kontakt AI Tags hat (höchste Priorität)
    if (contact.tags && typeof contact.tags === 'object') {
      console.log('📧 [BULK-MODE] AI Tags found in contact:', contact.tags);
      
      // Konvertiere Tags-Objekt zu Array und sortiere nach appliedAt (neueste zuerst)
      const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
        id: tagId,
        ...tagData
      }));
      
      // Sortiere nach appliedAt Datum (neueste zuerst)
      tagsArray.sort((a, b) => {
        const dateA = new Date(a.appliedAt || 0);
        const dateB = new Date(b.appliedAt || 0);
        console.log('📧 [BULK-MODE] 🔍 SORTING DEBUG:');
        console.log('📧 [BULK-MODE]   Tag A:', { id: a.id, appliedAt: a.appliedAt, parsedDate: dateA.toISOString(), timestamp: dateA.getTime() });
        console.log('📧 [BULK-MODE]   Tag B:', { id: b.id, appliedAt: b.appliedAt, parsedDate: dateB.toISOString(), timestamp: dateB.getTime() });
        console.log('📧 [BULK-MODE]   Sort result (dateB - dateA):', dateB.getTime() - dateA.getTime());
        return dateB - dateA; // Neueste zuerst
      });
      
      console.log('📧 [BULK-MODE] Tags array sorted by date:', tagsArray);
      console.log('📧 [BULK-MODE] 🎯 SELECTED TAG (index 0):', {
        id: tagsArray[0]?.id,
        appliedAt: tagsArray[0]?.appliedAt,
        appliedTopic: tagsArray[0]?.appliedTopic,
        selectedTopic: tagsArray[0]?.selectedTopic,
        isNewest: true
      });
      
      if (tagsArray.length > 0) {
        const newestTag = tagsArray[0];
        console.log('📧 [BULK-MODE] Newest tag:', newestTag);
        
        // 🔥 WICHTIG: Immer die neueste Version aus userTopics laden (nicht vom Kontakt)
        if (newestTag.id) {
          console.log('📧 [BULK-MODE] Searching for AI Tag with ID:', newestTag.id);
          console.log('📧 [BULK-MODE] Available userTopics keys:', Object.keys(userTopics));
          
          // 🔥 ZEIGE ALLE TOPICS MIT IHREN AI TAG IDS
          console.log('📧 [BULK-MODE] 🔍 COMPLETE userTopics breakdown:');
          Object.entries(userTopics).forEach(([topicKey, topicData]) => {
            console.log(`📧 [BULK-MODE]   Topic Key: ${topicKey}`);
            console.log(`📧 [BULK-MODE]   Topic Data:`, {
              topic: topicData.topic,
              aiTagId: topicData.aiTagId,
              type: topicData.type,
              subjectLine: topicData.subjectLine,
              createdAt: topicData.createdAt
            });
          });
          
          // 🔥 NEUE LOGIK: Lade die AI Tag Settings und hole selectedTopic von dort!
          // Problem: Der gespeicherte Tag hat kein selectedTopic, aber die AI Tag Settings haben es
          
          // Lade die AI Tag-Einstellungen
          const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${newestTag.id}`);
          const aiTagSnapshot = await new Promise((resolve) => {
            onValue(aiTagRef, resolve, { onlyOnce: true });
          });
          
          if (aiTagSnapshot.exists()) {
            const aiTagData = aiTagSnapshot.val();
            console.log('📧 [BULK-MODE] ✅ Loaded AI Tag settings from database:', {
              selectedTopic: aiTagData.selectedTopic,
              tag: aiTagData.tag,
              name: aiTagData.name
            });
            
            if (aiTagData.selectedTopic) {
              console.log('📧 [BULK-MODE] ✅ Found selectedTopic from AI Tag settings (like Normal-Mode):', aiTagData.selectedTopic);
              console.log('📧 [BULK-MODE] 📝 Note: Using AI Tag selectedTopic from settings, not from contact tag');
              return aiTagData.selectedTopic;
            }
          } else {
            console.log('📧 [BULK-MODE] ⚠️ AI Tag settings not found for ID:', newestTag.id);
          }
          
          // 🔥 FALLBACK: Prüfe, ob der AI Tag ein selectedTopic hat (falls es doch gespeichert wurde)
          if (newestTag.selectedTopic) {
            console.log('📧 [BULK-MODE] ✅ Found selectedTopic directly from stored tag:', newestTag.selectedTopic);
            return newestTag.selectedTopic;
          }
        }
        
        // Fallback: Prüfe, ob der gespeicherte Tag ein selectedTopic hat (alte Kopie)
        if (newestTag.selectedTopic) {
          console.log('📧 [BULK-MODE] ✅ Using selectedTopic from stored tag (legacy):', newestTag.selectedTopic);
          return newestTag.selectedTopic;
        }
      } else {
        console.log('📧 [BULK-MODE] No AI tags found in contact');
      }
    } else {
      console.log('📧 [BULK-MODE] No AI tags found in contact');
    }
    
    // 2. Fallback: Prüfe auch die alte aiTags Struktur (falls noch vorhanden)
    if (contact.aiTags && Array.isArray(contact.aiTags) && contact.aiTags.length > 0) {
      console.log('📧 [BULK-MODE] Checking legacy aiTags structure:', contact.aiTags);
      const lastTag = contact.aiTags[contact.aiTags.length - 1];
      if (lastTag.selectedTopic) {
        console.log('📧 [BULK-MODE] ✅ Using selectedTopic from legacy aiTags:', lastTag.selectedTopic);
        return lastTag.selectedTopic;
      }
    }
    
    console.log('📧 [BULK-MODE] No AI tag topics found, using status-based logic');
    
    // 3. Fallback: Verwende die bisherige Status-basierte Logik
    const contactStatus = effectiveContactStatus(contact);
    console.log('📧 [BULK-MODE] Contact status:', contactStatus);
    
    // Entscheidungslogik für das Topic basierend auf dem Status
    if (!contactStatus || contactStatus === "Not Contacted" || contactStatus === "New" || contactStatus === "Import") {
      console.log('📧 [BULK-MODE] Status indicates "Introduce Yourself"');
      return "Introduce Yourself";
    } else if (contactStatus === "Introduced") {
      console.log('📧 [BULK-MODE] Status indicates "Follow Up"');
      return "Follow Up";
    } else if (contactStatus === "First Follow-up" || contactStatus === "1. Follow-UP") {
      console.log('📧 [BULK-MODE] Status indicates "Second Follow Up"');
      return "Second Follow Up";
    } else if (contactStatus === "Second Follow-up" || contactStatus === "2. Follow-UP") {
      console.log('📧 [BULK-MODE] Status indicates "Third Follow Up"');
      return "Third Follow Up";
    } else {
      // Fallback für unbekannte Status
      console.log('📧 [BULK-MODE] Unknown status, defaulting to "Introduce Yourself"');
      return "Introduce Yourself";
    }
  };

  // Initialisiere den aktuellen Kontakt
  useEffect(() => {
    if (updatedBulkContacts.length > 0 && isTopicsLoaded) {
      const contactId = updatedBulkContacts[currentBulkIndex];
      const contact = allData.find(row => row.id === contactId);
      if (contact) {
        expectedContactIdRef.current = contactId;
        setCurrentBulkContact(contact);
        
        determineTopicForContact(contact).then(topic => {
          if (expectedContactIdRef.current !== contactId) return;
          console.log('📧 [BULK-CONTACT-CHANGE] Setting topic for contact:', contact.business_name, 'Topic:', topic);
          setSelectedTopic(topic);
        });
      }
    }
  }, [updatedBulkContacts, currentBulkIndex, allData, isTopicsLoaded]);
  
  // Initialisiere die Absender-E-Mail und starte die Bulk-Generierung
  useEffect(() => {
    if (senderEmails.length > 0) {
      const cid = updatedBulkContacts[currentBulkIndex];
      const mail = cid ? bulkGeneratedEmails[cid] : null;
      const preparedAccount = mail?._prepared
        ? pickPreparedSenderAccount({
            senderId: mail._preparedSenderId,
            senderEmail: mail._preparedSenderEmail,
          }, senderEmails)
        : null;
      // Prepared body already has this inbox's signature — don't restore lastUsedSender.
      if (preparedAccount) {
        setRandomSenderMode(false);
        setSelectedSenderEmail(preparedAccount.email);
      } else {
        const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
        onValue(lastSenderRef, (snapshot) => {
          // Prepared seed can land after this listener was armed — don't clobber From.
          if (hasPreparedEmailsRef.current) return;
          if (snapshot.exists()) {
            const lastSender = snapshot.val();
            const matchingEmail = senderEmails.find(email => email.email === lastSender.email);
            if (matchingEmail) {
              setSelectedSenderEmail(matchingEmail.email);
              return;
            }
          }
          setSelectedSenderEmail(senderEmails[0].email);
        }, { onlyOnce: true });
      }
    }
    
    // Starte automatisch die Bulk-Generierung, wenn alle Bedingungen erfüllt
    // sind — aber ERST nachdem wir geprüft haben, ob vorbereitete Flow-Emails
    // existieren (dann wird gar nicht generiert, sondern nur geseedet).
    if (
      updatedBulkContacts.length > 0 && 
      isTopicsLoaded && 
      !hasStartedBulkGeneration.current && 
      senderEmails.length > 0 &&
      preparedChecked
    ) {
      hasStartedBulkGeneration.current = true;
      setTimeout(() => {
        generateAllBulkEmails();
      }, 300);
    }
  }, [senderEmails, userId, updatedBulkContacts, isTopicsLoaded, preparedChecked]);

  // Load any backend-prepared flow emails for the selected contacts. When ALL
  // selected contacts have a prepared email, we seed them straight into the
  // Bulk-mode caches and skip live generation entirely (instant, no credits —
  // they were charged at prep time). Mixed selections fall back to normal
  // generation. Runs once per contact set.
  useEffect(() => {
    let cancelled = false;
    const loadPreparedEmails = async () => {
      if (!userId || !updatedBulkContacts || updatedBulkContacts.length === 0) {
        if (!cancelled) setPreparedChecked(true);
        return;
      }
      // Only ever run the initial seed once — later mutations of the contact set
      // (sends/skips) must not re-seed and clobber in-progress edits/sent state.
      if (preparedLoadStartedRef.current) return;
      preparedLoadStartedRef.current = true;

      // Cheap pre-filter: the contact rows already carry `has_prepared_email`,
      // so we only hit Firestore for contacts that actually have a prepared
      // email. In the common case (normal bulk send) this is zero reads.
      const candidateIds = presetEmails
        ? updatedBulkContacts.filter((cid) => presetEmails[cid])
        : updatedBulkContacts.filter((cid) => {
          const c = (allData || []).find((row) => row.id === cid);
          return c && c.has_prepared_email === true;
        });
      if (candidateIds.length === 0) {
        if (!cancelled) setPreparedChecked(true);
        return;
      }

      try {
        const results = presetEmails
          ? candidateIds.map((cid) => ({ cid, data: presetEmails[cid] }))
          : await Promise.all(
            candidateIds.map(async (cid) => {
              try {
                const s = await getDoc(doc(firestore, `World/${userId}/preparedEmails/${cid}`));
                return s.exists() ? { cid, data: s.data() } : null;
              } catch (_) {
                return null;
              }
            })
          );
        if (cancelled) return;

        const seed = {};
        const imgSeed = {};
        const preparedIds = new Set();
        results.forEach((r) => {
          if (!r || !r.data) return;
          const d = r.data;
          preparedIds.add(r.cid);
          seed[r.cid] = {
            subject: d.subject || '',
            body: d.body || '',
            // Precomputed brand-voice alternatives for the ⚡ button (Phase 2).
            // Persisted at prepare-time by processOutreachJobs / processFlowAutomation
            // so the dropdown opens instantly when the user reviews this email.
            subjectAlternatives: Array.isArray(d.subjectAlternatives) ? d.subjectAlternatives : [],
            _trackingTemplateId: d.tracking?.templateId || null,
            _trackingSubjectLineId: d.tracking?.subjectLineId || null,
            _trackingInstructionId: d.tracking?.instructionId || null,
            _trackingVariantId: d.tracking?.variantId || null,
            _prepared: true,
            _preparedSenderId: d.senderId || '',
            _preparedSenderEmail: d.senderEmail || '',
            _preparedEmailType: d.emailType || '',
            _preparedTopicName: d.topicName || '',
            ...(d.draftId ? {
              _safeGuardDraftId: d.draftId,
              _presetRecipient: d.recipient || '',
              _presetCc: Array.isArray(d.cc) ? d.cc : [],
              _presetAttachments: Array.isArray(d.attachments) ? d.attachments : [],
            } : {}),
          };
          if (d.aiImages && typeof d.aiImages === 'object' && Object.keys(d.aiImages).length > 0) {
            imgSeed[r.cid] = d.aiImages;
          }
        });

        // Prepared mode is active only when EVERY selected contact has a prepared
        // email — then we skip live generation entirely.
        const allPrepared = preparedIds.size > 0 && preparedIds.size === updatedBulkContacts.length;
        preparedContactsRef.current = preparedIds;
        hasPreparedEmailsRef.current = allPrepared;

        if (allPrepared) {
          preparedMetaRef.current = Object.fromEntries(
            Object.entries(seed)
              .filter(([, m]) => m._preparedEmailType || m._preparedTopicName)
              .map(([cid, m]) => [cid, { topicName: m._preparedTopicName, emailType: m._preparedEmailType }])
          );
          setBulkGeneratedEmails(seed);
          if (Object.keys(imgSeed).length > 0) {
            setBulkAiImages((prev) => ({ ...prev, ...imgSeed }));
          }
          setIsBulkGenerating(false);
          hasStartedBulkGeneration.current = true; // suppress auto-generation
          console.log(`📧 [PREPARED] Seeded ${preparedIds.size} pre-prepared flow emails — skipping live generation.`);
        }
      } catch (err) {
        console.warn('📧 [PREPARED] Failed to load prepared emails (falling back to generation):', err?.message || err);
      } finally {
        if (!cancelled) setPreparedChecked(true);
      }
    };
    loadPreparedEmails();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, updatedBulkContacts]);

  // Point From at the inbox that generated this prepared mail (footer + SMTP).
  // Re-sync only when the visible contact changes so a manual From pick stays.
  useEffect(() => {
    const cid = updatedBulkContacts[currentBulkIndex];
    if (!cid || preparedSenderSyncedForRef.current === cid) return;
    const mail = bulkGeneratedEmails[cid];
    if (!mail?._prepared) return;
    const account = pickPreparedSenderAccount({
      senderId: mail._preparedSenderId,
      senderEmail: mail._preparedSenderEmail,
    }, senderEmails);
    if (!account) return;
    preparedSenderSyncedForRef.current = cid;
    setRandomSenderMode(false);
    setSelectedSenderEmail(account.email);
  }, [currentBulkIndex, updatedBulkContacts, bulkGeneratedEmails, senderEmails]);

  // Keep the topic on the prepared draft's stage. The status-based guess can
  // resolve after the seed lands and overwrite it, so this re-applies until the
  // user picks a topic by hand.
  useEffect(() => {
    const cid = updatedBulkContacts[currentBulkIndex];
    if (!cid || manualTopicForRef.current.has(cid)) return;
    if (expectedContactIdRef.current && expectedContactIdRef.current !== cid) return;
    const meta = preparedMetaRef.current[cid];
    if (!meta || !bulkGeneratedEmails[cid]?._prepared) return;
    const topic = topicForPreparedDraft(meta, topicTypeMapping);
    if (topic && topic !== selectedTopic) setSelectedTopic(topic);
  }, [currentBulkIndex, updatedBulkContacts, bulkGeneratedEmails, selectedTopic, topicTypeMapping]);

  const currentPreparedAccount = React.useMemo(() => {
    const cid = currentBulkContact?.id;
    const mail = cid ? bulkGeneratedEmails[cid] : null;
    if (!mail?._prepared) return null;
    return pickPreparedSenderAccount({
      senderId: mail._preparedSenderId,
      senderEmail: mail._preparedSenderEmail,
    }, senderEmails);
  }, [currentBulkContact, bulkGeneratedEmails, senderEmails]);

  const activeTemplatePref = React.useMemo(
    () => getActiveTemplatePref(selectedTopic, userTopics, topicTypeMapping),
    [selectedTopic, userTopics]
  );
  const selectedTopicData = React.useMemo(
    () => findTopicInUserTopics(userTopics, selectedTopic),
    [selectedTopic, userTopics]
  );
  const activeDmFallback = React.useMemo(
    () => getActiveDmFallback(selectedTopic, userTopics, topicTypeMapping),
    [selectedTopic, userTopics]
  );

  // Shared CopyTwin Re-Generate state: is the active template a CopyTwin, what
  // is its default clone, and which trained clones can we switch to. Used by
  // both the 3-dots framework picker and the standalone quick popover.
  const cloneRegenState = React.useMemo(() => {
    const tpl = findTopicInUserTopics(userTopics, selectedTopic);
    const isCloneRegen = isVoiceCloneTopicData(tpl);
    const defaultCloneId = tpl?.cloneConfig?.cloneId || null;
    const switchableClones = isCloneRegen
      ? [...(copyClones || [])]
          .filter(c => c && c.id && (c.voiceDNA || c.status === 'ready' || c.id === defaultCloneId))
          .sort((a, b) => (a.id === defaultCloneId ? -1 : b.id === defaultCloneId ? 1 : 0))
      : [];
    return { isCloneRegen, defaultCloneId, switchableClones };
  }, [selectedTopic, userTopics, copyClones]);

  const cloneRegenLabel = (c) => c?.name?.trim() || autoCloneName(c) || 'CopyTwin';

  const currentPresetRecipient = currentBulkContact
    ? bulkGeneratedEmails[currentBulkContact.id]?._presetRecipient || ''
    : '';

  useEffect(() => {
    if (currentBulkContact) {
      const contactEmails = getAllContactEmails(currentBulkContact);
      // Never auto-select an address that hard-bounced last time.
      const bounced = currentBulkContact.emailBounceCheck;
      const bouncedEmail = (bounced && bounced.action === 'undeliverable' && bounced.originalEmail)
        ? String(bounced.originalEmail).toLowerCase() : null;
      const deliverableEmails = bouncedEmail
        ? contactEmails.filter(e => e.email && e.email.toLowerCase() !== bouncedEmail)
        : contactEmails;
      let resolvedEmail = "";
      if (currentPresetRecipient) {
        resolvedEmail = currentPresetRecipient;
        setSelectedEmail(resolvedEmail);
        setCcEmails(bulkGeneratedEmails[currentBulkContact.id]?._presetCc || []);
      } else if (deliverableEmails.length > 0) {
        const preferred = resolvePreferredEmail(currentBulkContact, activeTemplatePref || 'auto', { dmFallback: activeDmFallback });
        const preferredOk = preferred && preferred.email &&
          (!bouncedEmail || preferred.email.toLowerCase() !== bouncedEmail);
        resolvedEmail = preferredOk ? preferred.email : deliverableEmails[0].email;
        setSelectedEmail(resolvedEmail);
        setCcEmails([]);
      } else {
        setSelectedEmail("");
        setCcEmails([]);
      }

      const cfCtxDisplay = (customContextFields || [])
        .map(cf => { const v = currentBulkContact[cf.key]; return v ? `${cf.label}: ${v}` : ''; })
        .filter(Boolean).join('\n');

      const selectedDm = contactEmails.find(e => e.email === resolvedEmail && e.source === 'decision_maker');
      const dmContext = selectedDm
        ? `\nRecipient: ${selectedDm.dmName}${selectedDm.dmRole ? ` (${selectedDm.dmRole})` : ''} — address this person by name`
        : '';

      // Standard lead fields are shown as elegant cards; textarea holds only the
      // editable description + extra context (re-attached for AI via composeAiContext).
      const contextText = `${currentBulkContact.lead_description || ''}${dmContext}${cfCtxDisplay ? `\n${cfCtxDisplay}` : ''}`.trim();
      setContextInfo(contextText);

      setSendSuccess(false);
      setIsSending(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBulkContact, customContextFields, activeTemplatePref, activeDmFallback, currentPresetRecipient]);

  // Update context when user switches recipient email (DM ↔ general)
  useEffect(() => {
    if (!currentBulkContact || !selectedEmail) return;
    const contactEmails = getAllContactEmails(currentBulkContact);
    const selectedDm = contactEmails.find(e => e.email === selectedEmail && e.source === 'decision_maker');
    const cfCtxDisplay = (customContextFields || [])
      .map(cf => { const v = currentBulkContact[cf.key]; return v ? `${cf.label}: ${v}` : ''; })
      .filter(Boolean).join('\n');
    const dmContext = selectedDm
      ? `\nRecipient: ${selectedDm.dmName}${selectedDm.dmRole ? ` (${selectedDm.dmRole})` : ''} — address this person by name`
      : '';
    const contextText = `${currentBulkContact.lead_description || ''}${dmContext}${cfCtxDisplay ? `\n${cfCtxDisplay}` : ''}`.trim();
    setContextInfo(contextText);
  }, [selectedEmail]);

  // Re-attach standard lead fields (shown as cards) to the editable context so
  // the AI always receives complete lead info for the previewed contact.
  const composeAiContext = () => {
    if (!currentBulkContact) return contextInfo;
    const header = [
      currentBulkContact.business_name ? `Business Name: ${currentBulkContact.business_name}` : '',
      currentBulkContact.lead_city ? `City: ${currentBulkContact.lead_city}` : '',
      currentBulkContact.lead_type ? `Type: ${currentBulkContact.lead_type}` : '',
      currentBulkContact.lead_website ? `Website: ${currentBulkContact.lead_website}` : '',
    ].filter(Boolean).join('\n');
    return [header, contextInfo].filter(Boolean).join('\n').trim();
  };

  // ── Per-contact dynamic AI images ──────────────────────────────────────────
  const setContactImage = (contactId, token, val) =>
    setBulkAiImages((prev) => ({
      ...prev,
      [contactId]: { ...(prev[contactId] || {}), [token]: val },
    }));

  const clearContactImage = (contactId, token) =>
    setBulkAiImages((prev) => {
      const cur = prev[contactId];
      if (!cur || !(token in cur)) return prev;
      const next = { ...cur };
      delete next[token];
      return { ...prev, [contactId]: next };
    });

  // Cancel queued prefetch work once Bulk mode is gone.
  useEffect(() => () => { bulkImagesAbortedRef.current = true; }, []);

  const generateOneBulkImage = async (contact, token, asset, force, extra = {}) => {
    const stateKey = `${contact.id}::${token}`;
    // Checked by the concurrency gate right before a queued job starts, and
    // again once it resolves.
    const isObsolete = () =>
      bulkImagesAbortedRef.current ||
      removedBulkImageTokensRef.current.has(stateKey) ||
      sentContactsRef.current.has(contact.id);
    try {
      const gen = await generateAiImageForContact({
        userId,
        token,
        contactId: contact.id,
        contact,
        userSettings,
        force,
        priority: IMAGE_PRIORITY_ACTIVE,
        shouldSkip: isObsolete,
        ...extra,
      });
      if (isObsolete()) return; // cancelled
      setContactImage(contact.id, token, {
        status: "ready",
        imageUrl: gen.imageUrl,
        altText: gen.altText,
        maxWidth: gen.maxWidth,
        align: gen.align,
        linkUrl: gen.linkUrl,
        width: gen.width,
        height: gen.height,
        name: (asset && asset.name) || token,
      });
    } catch (err) {
      if (bulkImagesAbortedRef.current) return; // modal closed — nothing left to update
      if (removedBulkImageTokensRef.current.has(stateKey)) return; // user removed it
      if (isSkippedImageError(err)) {
        // Dropped while still queued (contact sent meanwhile). Clear the
        // "loading" marker, otherwise returning to this contact would leave the
        // send button blocked on an image that is never coming.
        clearContactImage(contact.id, token);
        return;
      }
      console.error(`[BulkMode] AI image "${token}" failed:`, err);
      setContactImage(contact.id, token, {
        status: "error",
        error: err.message || "Generation failed",
        name: (asset && asset.name) || token,
      });
    }
  };

  // Generate all AI images referenced in a contact's body (lazy: only for the
  // contact currently being viewed). Skips tokens already generated.
  const triggerBulkAiImages = (contact, bodyText, priority = IMAGE_PRIORITY_ACTIVE) => {
    if (!contact) return;
    const contactId = contact.id;
    const tokens = collectAiImageTokens(bodyText, userSettings?.imageLabAssets);
    if (tokens.length === 0) return;
    const current = bulkAiImages[contactId] || {};
    const pending = tokens.filter(({ token }) => {
      const s = current[token];
      return !(s && (s.status === "ready" || s.status === "loading"));
    });
    if (pending.length === 0) return;
    setBulkAiImages((prev) => {
      const cur = { ...(prev[contactId] || {}) };
      pending.forEach(({ token, asset }) => {
        removedBulkImageTokensRef.current.delete(`${contactId}::${token}`);
        cur[token] = { status: "loading", name: asset.name || token };
      });
      return { ...prev, [contactId]: cur };
    });
    pending.forEach(({ token, asset }) => generateOneBulkImage(contact, token, asset, false, { priority }));
  };

  const regenerateBulkImage = (token) => {
    const contact = currentBulkContact;
    if (!contact) return;
    const asset = userSettings?.imageLabAssets?.[token];
    removedBulkImageTokensRef.current.delete(`${contact.id}::${token}`); // re-enable
    setContactImage(contact.id, token, { status: "loading", name: (asset && asset.name) || token });
    generateOneBulkImage(contact, token, asset, true);
  };

  // Refine (ChatGPT-style): feed the current image back to the model with the
  // user's change request and replace it inline for this contact.
  const refineBulkImage = (token, refinePrompt) => {
    const contact = currentBulkContact;
    if (!contact) return;
    const asset = userSettings?.imageLabAssets?.[token];
    const current = (bulkAiImages[contact.id] || {})[token];
    const refineImageUrl = current?.imageUrl;
    if (!refineImageUrl || !refinePrompt || !String(refinePrompt).trim()) return;
    removedBulkImageTokensRef.current.delete(`${contact.id}::${token}`); // re-enable
    setContactImage(contact.id, token, { status: "loading", name: (asset && asset.name) || token });
    generateOneBulkImage(contact, token, asset, true, { refinePrompt, refineImageUrl });
  };

  // Remove: drop image state AND strip the {{image:token}} from both the live
  // body and the stored generated email so it never comes back or gets sent.
  const removeBulkImage = (token) => {
    const contact = currentBulkContact;
    if (!contact) return;
    const contactId = contact.id;
    // Mark cancelled so any in-flight generation for this contact/token is discarded.
    removedBulkImageTokensRef.current.add(`${contactId}::${token}`);
    setBulkAiImages((prev) => {
      const cur = { ...(prev[contactId] || {}) };
      delete cur[token];
      return { ...prev, [contactId]: cur };
    });
    const stripRe = new RegExp(`\\n?\\{\\{\\s*image:${token}\\s*\\}\\}\\n?`, "g");
    setBodyContent((prev) => prev.replace(stripRe, "\n"));
    setBulkGeneratedEmails((prev) => {
      const e = prev[contactId];
      if (!e) return prev;
      return { ...prev, [contactId]: { ...e, body: (e.body || "").replace(stripRe, "\n") } };
    });
  };

  // Auto-trigger image generation for the current contact once its text is ready.
  useEffect(() => {
    const contact = currentBulkContact;
    if (!contact) return;
    const gen = bulkGeneratedEmails[contact.id];
    if (gen && !gen.error && gen.body) {
      // This contact may already be queued from the look-ahead prefetch, in
      // which case triggerBulkAiImages skips it as "loading" — promote it so it
      // stops yielding to the warming jobs the user is not waiting for.
      collectAiImageTokens(gen.body, userSettings?.imageLabAssets).forEach(({ token }) =>
        promoteImageGeneration(contact.id, token)
      );
      triggerBulkAiImages(contact, gen.body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBulkContact, bulkGeneratedEmails, userSettings?.imageLabAssets]);

  // Look-ahead image prefetch: an AI image can take up to ~3 min to render, so
  // waiting until the user reaches a contact means a long stall. Mirroring the
  // TEXT preload, we warm the AI images of the next few UNSENT contacts in the
  // background as soon as their text is ready. Because the backend caches each
  // generated image per contact (imageLabCache), navigating there is then an
  // instant cache-hit instead of a fresh 3-minute generation.
  //
  // Sizing the window: the user only ever stalls if they advance faster than the
  // warming keeps up, i.e. the dwell time per contact must be at least
  // (generation time / window). At the ~130 s the current templates need, a
  // window of 7 means ~19 s per contact — enough to read and send — whereas a
  // window of 3 would already stall below ~43 s. The window is rolling and only
  // moves with the user, so aborting a 100-contact run wastes at most one
  // window's worth, not the whole run. Prefetch runs at the lower priority of
  // the concurrency gate in dynamicImageUtils, so the contact on screen never
  // waits behind warming work.
  useEffect(() => {
    const IMAGE_PREFETCH_LOOKAHEAD = 7; // how many upcoming contacts to warm
    const assets = userSettings?.imageLabAssets;
    if (!assets || typeof assets !== "object") return;
    if (!Array.isArray(updatedBulkContacts) || updatedBulkContacts.length === 0) return;

    // Collect the next few unsent contacts after the current position — the
    // same order the user navigates via goToNextBulkContact.
    const upcoming = [];
    for (
      let i = currentBulkIndex + 1;
      i < updatedBulkContacts.length && upcoming.length < IMAGE_PREFETCH_LOOKAHEAD;
      i++
    ) {
      const cid = updatedBulkContacts[i];
      if (!isContactSent(cid)) upcoming.push(cid);
    }
    if (upcoming.length === 0) return;

    upcoming.forEach((cid) => {
      const gen = bulkGeneratedEmails[cid];
      if (!gen || gen.error || !gen.body) return; // text not ready yet → skip
      const contact = allData.find((row) => row.id === cid);
      // triggerBulkAiImages de-dupes (skips tokens already ready/loading), so
      // re-running this effect as more texts land is cheap and idempotent.
      if (contact) triggerBulkAiImages(contact, gen.body, IMAGE_PRIORITY_PREFETCH);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBulkIndex, bulkGeneratedEmails, updatedBulkContacts, allData, sentEmails, userSettings?.imageLabAssets]);

  useEffect(() => {
    if (!currentBulkContact) return;
    const contactId = currentBulkContact.id;
    const contactChanged = lastContactId !== contactId;
    
    setSendSuccess(false);
    
    if (bulkGeneratedEmails[contactId]) {
      const generatedEmail = bulkGeneratedEmails[contactId];
      if (!generatedEmail.error) {
        if (contactChanged || !userEditedSubject) {
          updateSubjectInputDirectly(generatedEmail.subject || "");
        }
        if (contactChanged && !userEditedBody) {
          setBodyContent(generatedEmail.body || "");
        } else if (bodyContent === "" || !bodyContent) {
          setBodyContent(generatedEmail.body || "");
        }
        // Restore precomputed brand-voice alternatives for the ⚡ button.
        // Load either when scrolling to a new contact OR when this contact's
        // async initial-batch generation just finished (subjectAlts still
        // empty). Never overwrite alts the user just fetched via ⚡.
        const cachedAlts = Array.isArray(generatedEmail.subjectAlternatives) ? generatedEmail.subjectAlternatives : [];
        if (contactChanged) {
          setSubjectAlts(cachedAlts);
          setSubjectAltsPrecomputed(cachedAlts.length > 0);
          setSubjectAltsOpen(false);
        } else if (cachedAlts.length > 0 && subjectAlts.length === 0 && !subjectAltsLoading && !subjectAltsOpen) {
          setSubjectAlts(cachedAlts);
          setSubjectAltsPrecomputed(true);
        }
      } else {
        if (contactChanged || !userEditedSubject) {
          determineTopicForContact(currentBulkContact).then(topic => {
            if (expectedContactIdRef.current !== contactId) return;
            setSelectedTopic(topic);
            updateSubjectInputDirectly(getTopicSubjectLine(currentBulkContact, topic));
          });
        }
        if ((bodyContent === "" || !bodyContent) && !userEditedBody) {
          setBodyContent("");
        }
      }
    } else {
      if (contactChanged || !userEditedSubject) {
        determineTopicForContact(currentBulkContact).then(topic => {
          if (expectedContactIdRef.current !== contactId) return;
          setSelectedTopic(topic);
          updateSubjectInputDirectly(getTopicSubjectLine(currentBulkContact, topic));
        });
      }
      if ((bodyContent === "" || !bodyContent) && !userEditedBody) {
        setBodyContent("");
      }
    }
    
    if (contactChanged) {
      setLastContactId(contactId);
      setUserEditedSubject(false);
      setUserEditedBody(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBulkContact, bulkGeneratedEmails, userTopics]);

  // Hilfsfunktion für zufällige Auswahl aus Array (BulkMode)
  const getRandomFromArray = (array) => {
    const result = selectRandomWithTracking(array);
    return result ? result.text : null;
  };
  
  // 📧 Tracking-Info für einen Kontakt ermitteln
  const getTrackingInfoForContact = (contact, topic) => {
    // Finde das passende Template in userTopics
    let templateId = null;
    let templateName = null;
    let subjectLineId = null;
    let instructionId = null;
    // Voice-Clone-mode sends carry a variantId so the backend can credit
    // per-structure-variant stats. Standard sends leave it null.
    const generatedEmailForVariant = bulkGeneratedEmails[contact.id];
    const variantId = generatedEmailForVariant?._trackingVariantId || null;

    // Backend-prepared emails carry the EXACT template + subject/instruction IDs
    // the email was generated from. Use them verbatim so send-stats are accurate
    // (never re-derive from the current topic, which may resolve differently).
    if (generatedEmailForVariant?._prepared && generatedEmailForVariant._trackingTemplateId) {
      const tid = generatedEmailForVariant._trackingTemplateId;
      const td = (userTopics && userTopics[tid])
        || (userTopics?.customTopics && userTopics.customTopics[tid])
        || (userTopics?.selectedTopics && userTopics.selectedTopics[tid])
        || null;
      return {
        templateId: tid,
        templateName: td ? (td.topic || td.type) : (topic || null),
        subjectLineId: generatedEmailForVariant._trackingSubjectLineId || null,
        instructionId: generatedEmailForVariant._trackingInstructionId || null,
        variantId,
      };
    }

    // Hole den topicType für die Suche (z.B. "Introduce Yourself" → "intro")
    const topicType = topicTypeMapping[topic];
    console.log('📧 [BULK-TRACKING] Looking for template, topic:', topic, '→ type:', topicType);
    
    // Hilfsfunktion um zu prüfen, ob ein Topic passt
    const matchesTopic = (topicData) => {
      return topicData.topic === topic || 
             topicData.type === topic || 
             (topicType && topicData.type === topicType);
    };
    
    // Suche das Template
    for (const [topicId, topicData] of Object.entries(userTopics)) {
      if (matchesTopic(topicData)) {
        templateId = topicId;
        templateName = topicData.topic || topicData.type;
        console.log('📧 [BULK-TRACKING] Found template in userTopics:', topicId, templateName);
        
        // Subject Line ID ermitteln (falls bereits generiert)
        const generatedEmail = bulkGeneratedEmails[contact.id];
        if (generatedEmail && generatedEmail._trackingSubjectLineId) {
          subjectLineId = generatedEmail._trackingSubjectLineId;
        } else if (topicData.subjectLines && Array.isArray(topicData.subjectLines) && topicData.subjectLines.length > 0) {
          // Wenn keine ID gespeichert, nehme die erste (oder null)
          const first = topicData.subjectLines[0];
          subjectLineId = first && first.id ? first.id : `legacy_${topicId}`;
        } else if (topicData.subjectLine) {
          subjectLineId = `legacy_${topicId}`;
        }
        
        // Instruction ID ermitteln
        if (generatedEmail && generatedEmail._trackingInstructionId) {
          instructionId = generatedEmail._trackingInstructionId;
        } else if (topicData.topicSpecificNotes && Array.isArray(topicData.topicSpecificNotes) && topicData.topicSpecificNotes.length > 0) {
          const first = topicData.topicSpecificNotes[0];
          instructionId = first && first.id ? first.id : `legacy_${topicId}`;
        } else if (topicData.topicSpecificNote) {
          instructionId = `legacy_${topicId}`;
        }
        
        break;
      }
    }
    
    // Suche auch in selectedTopics und customTopics
    if (!templateId) {
      const searchIn = [userTopics.selectedTopics, userTopics.customTopics].filter(Boolean);
      for (const topics of searchIn) {
        for (const [topicId, topicData] of Object.entries(topics)) {
          if (matchesTopic(topicData)) {
            templateId = topicId;
            templateName = topicData.topic || topicData.type;
            console.log('📧 [BULK-TRACKING] Found template in nested topics:', topicId, templateName);
            break;
          }
        }
        if (templateId) break;
      }
    }
    
    return { templateId, templateName, subjectLineId, instructionId, variantId };
  };

  // Betreffzeile aus Topic-Einstellungen oder Standard-Templates holen
  const getTopicSubjectLine = (contact, topicOverride) => {
    if (!contact) return "Partnerschaftsanfrage";
    const effectiveTopic = topicOverride || selectedTopic;
    
    console.log('📧 [BULK-SUBJECT] Getting subject line for contact:', contact.business_name);
    console.log('📧 [BULK-SUBJECT] effectiveTopic:', effectiveTopic);
    
    // Follow-up reply-to-previous: short-circuit for any matching topic
    const replyTopic = findTopicInUserTopics(userTopics, effectiveTopic);
    if (replyTopic && replyTopic.subjectMode === 'reply_to_previous') {
      const replySubject = resolveFollowUpSubject(replyTopic, contact);
      if (replySubject) {
        console.log('📧 [BULK-SUBJECT] ✅ Using reply-to-previous subject:', replySubject);
        return replySubject;
      }
      console.warn('📧 [BULK-SUBJECT] reply_to_previous requested but contact has no lead_last_email.subject — falling back');
    }

    // 🔥 DIREKTE AI TAG PRÜFUNG: Genau wie im Normal-Modus
    // Prüfe, ob der Kontakt AI Tags mit selectedTopic hat
    if (contact.tags && typeof contact.tags === 'object') {
      const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
        id: tagId,
        ...tagData
      }));
      
      // Sortiere nach appliedAt Datum (neueste zuerst)
      tagsArray.sort((a, b) => {
        const dateA = new Date(a.appliedAt || 0);
        const dateB = new Date(b.appliedAt || 0);
        return dateB - dateA;
      });
      
      if (tagsArray.length > 0) {
        const newestTag = tagsArray[0];
        if (newestTag.selectedTopic && newestTag.selectedTopic === effectiveTopic) {
          console.log('📧 [BULK-SUBJECT] Topic matches AI Tag selectedTopic, searching userTopics...');
          
          const matchingTopic = findTopicInUserTopics(userTopics, effectiveTopic);
          
          if (matchingTopic) {
            let subject = "";
            
            // Neue Logik: Prüfe zuerst auf mehrere Subject Lines (Array)
            if (matchingTopic.subjectLines && Array.isArray(matchingTopic.subjectLines) && matchingTopic.subjectLines.length > 0) {
              console.log('🎲 [BULK-RANDOM] Multiple subject lines found, selecting random:', matchingTopic.subjectLines);
              const randomSubjectLine = getRandomFromArray(matchingTopic.subjectLines);
              console.log('🎲 [BULK-RANDOM] Selected random subject line:', randomSubjectLine);
              subject = randomSubjectLine;
            }
            // Backwards compatibility: Fallback auf einzelne Subject Line
            else if (matchingTopic.subjectLine) {
              console.log('🎲 [BULK-RANDOM] Single subject line found:', matchingTopic.subjectLine);
              subject = matchingTopic.subjectLine;
            }
            
            if (subject) {
              subject = subject.replace(/{{business_name}}/g, contact.business_name || 'Ihr Unternehmen');
              subject = subject.replace(/{{name}}/g, getFirstDmName(contact) || 'dort');
              subject = subject.replace(/{{company}}/g, 'BonusClub');
              if (hasInstructionBlocks(subject)) subject = stripInstructionBlocks(subject);
              console.log('📧 [BULK-SUBJECT] ✅ Using AI Tag subject line:', subject);
              return subject;
            }
          }
        }
      }
    }
    
    console.log('📧 [BULK-SUBJECT] Using standard logic');
    
    const matchingTopicFallback = findTopicInUserTopics(userTopics, effectiveTopic);
    if (matchingTopicFallback) {
      let subject = "";
      
      // Neue Logik: Prüfe zuerst auf mehrere Subject Lines (Array)
      if (matchingTopicFallback.subjectLines && Array.isArray(matchingTopicFallback.subjectLines) && matchingTopicFallback.subjectLines.length > 0) {
        console.log('🎲 [BULK-RANDOM] Multiple subject lines found in fallback, selecting random:', matchingTopicFallback.subjectLines);
        const randomSubjectLine = getRandomFromArray(matchingTopicFallback.subjectLines);
        console.log('🎲 [BULK-RANDOM] Selected random subject line in fallback:', randomSubjectLine);
        subject = randomSubjectLine;
      }
      // Backwards compatibility: Fallback auf einzelne Subject Line
      else if (matchingTopicFallback.subjectLine) {
        console.log('🎲 [BULK-RANDOM] Single subject line found in fallback:', matchingTopicFallback.subjectLine);
        subject = matchingTopicFallback.subjectLine;
      }
      
      if (subject) {
        subject = subject.replace(/{{business_name}}/g, contact.business_name || 'Ihr Unternehmen');
        subject = subject.replace(/{{name}}/g, getFirstDmName(contact) || 'dort');
        subject = subject.replace(/{{company}}/g, 'BonusClub');
        if (hasInstructionBlocks(subject)) subject = stripInstructionBlocks(subject);
        return subject;
      }
    }
    
    // Standard-Betreffzeilen
    return getDefaultSubject(contact, topicOverride);
  };

  const getDefaultSubject = (contact, topicOverride) => {
    if (!contact) return "Partnerschaftsanfrage";
    const effectiveTopic = topicOverride || selectedTopic;
    
    const defaultSubjects = {
      "Introduce Yourself": `Boost für ${contact.business_name || 'Ihr Unternehmen'}`,
      "Follow Up": `Nachfrage - Partnerschaft mit ${contact.business_name || 'Ihrem Unternehmen'}`,
      "Second Follow Up": `Kurzes Update für ${contact.business_name || 'Ihr Unternehmen'}`,
      "Third Follow Up": `Finale Nachfrage für ${contact.business_name || 'Ihr Unternehmen'}`
    };
    
    return defaultSubjects[effectiveTopic] || `Partnerschaftsanfrage für ${contact.business_name || 'Ihr Unternehmen'}`;
  };

  // Hilfsfunktion für Standard-Topic-Anweisungen
  const getDefaultTopicNote = (topicType) => {
    const defaultNotes = {
      'intro': "Bitte stelle unser Unternehmen vor, teile einige spannende Details darüber, wie wir helfen können, und füge einen Call-to-Action hinzu.",
      'follow_up_1': "Erkundige dich höflich, ob der Empfänger die vorherige E-Mail erhalten hat. Halte es kurz und freundlich.",
      'follow_up_2': "Dies ist das zweite Follow-up. Frage höflich nach, ob der Empfänger noch interessiert ist, ohne aufdringlich zu sein.",
      'follow_up_3': "Dies ist das letzte Follow-up. Mache einen letzten sanften Versuch, den Empfänger zu erreichen.",
      'custom_reply': "Bitte verfasse eine durchdachte Antwort auf die Nachricht des Empfängers."
    };
    
    return defaultNotes[topicType] || "Bitte schreibe eine professionelle E-Mail an den Empfänger.";
  };
  
  const handleRegenerateSubjectLines = async () => {
    if (subjectAltsLoading) return;
    // Precomputed alternatives from the last generation for this contact
    // sit in state — first click opens them instantly (no API round-trip).
    // Second click clears the flag so we fetch fresh brand-voice alternatives.
    if (subjectAltsPrecomputed && subjectAlts.length > 0 && !subjectAltsOpen) {
      setSubjectAltsOpen(true);
      setSubjectAltsPrecomputed(false);
      return;
    }
    if (subjectAltsOpen && subjectAlts.length > 0) {
      setSubjectAltsOpen(false);
      return;
    }
    setSubjectAltsOpen(true);
    setSubjectAltsLoading(true);
    setSubjectAlts([]);
    setSubjectAltsPrecomputed(false);

    const contact = currentBulkContact;
    if (!contact) { setSubjectAltsLoading(false); return; }

    try {
      const topicId = Object.keys(userTopics).find(id => {
        const t = userTopics[id];
        return t.topic === selectedTopic || t.type === (topicTypeMapping[selectedTopic] || selectedTopic);
      });
      const topicData = topicId ? userTopics[topicId] : {};
      const language = topicData.language || aiSettings.language || 'auto';
      const mergedInstructions = [aiSettings.subjectLineGuidelines, topicData.smartSubjectInstructions].filter(Boolean).join('\n') || '';

      // Subject variants the user authored WITH {instruction} prompts. If present,
      // the backend resolves those prompts (against the finished body) instead of
      // the generic suggestion prompt — so the button honors what the user crafted.
      const bulkReplaceMerge = (text) => (text || '')
        .replace(/{{business_name}}/g, contact.business_name || 'Ihr Unternehmen')
        .replace(/{{name}}/g, getFirstDmName(contact) || 'dort')
        .replace(/{{company}}/g, 'BonusClub');
      const subjectInstrTemplates = (Array.isArray(topicData.subjectLines) ? topicData.subjectLines : [])
        .map(sl => (sl && typeof sl === 'object' ? sl.text : sl))
        .filter(t => typeof t === 'string' && t.trim() && hasInstructionBlocks(t))
        .map(t => bulkReplaceMerge(t));

      // Static subject variants (no {instruction}) — brand-voice reference for the suite.
      const staticVoiceLines = (Array.isArray(topicData.subjectLines) ? topicData.subjectLines : [])
        .map(sl => (sl && typeof sl === 'object' ? sl.text : sl))
        .filter(t => typeof t === 'string' && t.trim() && !hasInstructionBlocks(t))
        .map(t => bulkReplaceMerge(t));

      const bulkContext = [
        `Business: ${contact.business_name || ''}`,
        contact.lead_city ? `City: ${contact.lead_city}` : '',
        contact.lead_description ? `Description: ${contact.lead_description}` : '',
        contact.lead_type ? `Type: ${contact.lead_type}` : '',
        contact.lead_website ? `Website: ${contact.lead_website}` : '',
      ].filter(Boolean).join('\n');

      // CopyTwin-aware suggestions: when the active template is a Voice-Clone
      // template, pass the configured clone + campaign brief so the backend
      // generates the 4 alternatives in the writer's trained subject voice
      // (DNA, tactics, ✓/✗ calibration) instead of the generic prompt.
      const cloneSubjectContext = cloneRegenState.isCloneRegen && cloneRegenState.defaultCloneId
        ? {
            cloneId: cloneRegenState.defaultCloneId,
            cloneConfig: topicData.cloneConfig || null,
            userId,
            senderName: userSettings?.user_name || '',
            recipientCity: contact.lead_city || '',
            recipientCountry: contact.lead_country || '',
            recipientWebsite: contact.lead_website || '',
            businessDescription: contact.lead_description || '',
          }
        : {};

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateSubjectLines(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: contact.business_name || '',
          firstName: getFirstDmName(contact),
          context: bulkContext,
          language,
          recipientName: getFirstDmName(contact) || '',
          recipientRole: '',
          style: topicData.smartSubjectStyle || 'neutral',
          userInstructions: mergedInstructions,
          existingSubjects: [],
          customFieldsData: (customContextFields || []).reduce((acc, cf) => {
            const val = contact[cf.key];
            if (val != null && val !== '') acc.push({ key: cf.key, label: cf.label, value: String(val) });
            return acc;
          }, []),
          currentSubject: subjectLine,
          emailBody: bodyContent || '',
          writingLevel: topicData.writingLevel || 'middle_school',
          senderInfo: topicData.generalInfo || '',
          topicName: selectedTopic || '',
          topicSpecificNote: (Array.isArray(topicData.topicSpecificNotes) && topicData.topicSpecificNotes[0])
            ? (typeof topicData.topicSpecificNotes[0] === 'object' ? topicData.topicSpecificNotes[0].text : topicData.topicSpecificNotes[0])
            : (topicData.topicSpecificNote || ''),
          subjectLineTemplates: subjectInstrTemplates,
          staticSubjectLinesForVoice: staticVoiceLines,
          ...cloneSubjectContext
        })
      });

      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setSubjectAlts(data.subjectLines || []);
    } catch (err) {
      console.error('Subject line generation failed:', err);
      setSubjectAlts([]);
    } finally {
      setSubjectAltsLoading(false);
    }
  };

  const handleSelectSubjectAlt = (alt) => {
    setSubjectLine(alt);
    setUserEditedSubject(true);
    setSubjectAltsOpen(false);
    setSubjectAltsPrecomputed(false);
  };

  const handleRegenerateForCurrentContact = async (frameworkPromptOverride, customInstructions, cloneIdOverride) => {
    if (!currentBulkContact || isGenerating) return;
    
    const contactId = currentBulkContact.id;
    const contact = currentBulkContact;
    const previousInstructionId = bulkGeneratedEmails[contactId]?._trackingInstructionId || null;
    setIsGenerating(true);
    setBodyContent("");
    setUserEditedSubject(false);
    setUserEditedBody(false);
    // Stale precomputed alts belong to the previous body — clear the flag now
    // so a ⚡ click during regen fetches fresh brand-voice options instead of
    // showing options that no longer match. Fresh alts get seeded on success.
    setSubjectAltsPrecomputed(false);
    
    let contactTopic = selectedTopic;
    try {
      contactTopic = await determineTopicForContact(contact);
      console.log('📧 [BULK-REGENERATE] Contact:', contact.business_name, 'Topic:', contactTopic, 'Excluding instruction:', previousInstructionId);
      
      const emailContent = await generateEmailForContact(
        contact, 
        contactTopic,
        composeAiContext(), 
        userId, 
        userSettings, 
        aiSettings, 
        userTopics, 
        topicTypeMapping, 
        getDefaultTopicNote,
        customContextFields,
        getRecipientInfoForContact(contact, selectedEmail),
        frameworkPromptOverride,
        { excludeInstructionId: previousInstructionId, customInstructions, cloneIdOverride: cloneIdOverride || null, textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)) }
      );
      
      setBulkGeneratedEmails(prev => ({
        ...prev,
        [contactId]: emailContent
      }));
      
      if (expectedContactIdRef.current !== contactId) return;
      updateSubjectInputDirectly(emailContent.subject || "");
      setBodyContent(emailContent.body || "");
      // Fresh precomputed alternatives for the current contact — the ⚡
      // button opens them instantly on first click without an API round-trip.
      const freshAlts = Array.isArray(emailContent.subjectAlternatives) ? emailContent.subjectAlternatives : [];
      setSubjectAlts(freshAlts);
      setSubjectAltsPrecomputed(freshAlts.length > 0);
      setSubjectAltsOpen(false);
      
    } catch (error) {
      console.error(`Error generating email for contact ${contactId}:`, error);
      
      const isCreditErr = (error.message || '').includes('Not enough credits');
      if (isCreditErr) {
        setCreditError({ exhausted: true, available: 0, total: updatedBulkContacts.length });
      }
      
      const fallbackSubject = getTopicSubjectLine(contact, contactTopic);
      setBulkGeneratedEmails(prev => ({
        ...prev,
        [contactId]: { 
          error: true, 
          creditError: isCreditErr,
          message: isCreditErr ? 'Not enough credits' : error.message,
          subject: fallbackSubject
        }
      }));
      
      if (expectedContactIdRef.current !== contactId) return;
      updateSubjectInputDirectly(fallbackSubject);
    } finally {
      setIsGenerating(false);
    }
  };

  // Build creativity-dependent prompt for perfect mode
  const getPerfectPrompt = (level) => {
    if (level <= 2) return `Take the existing email content and only fix typos, grammar errors, and minor phrasing issues. Keep the text almost identical — same structure, same words, same tone. Only correct obvious mistakes.`;
    if (level <= 4) return `Take the existing email content and polish it. Fix any errors, improve word choices, and enhance clarity while keeping the same structure, tone, and core message. Small refinements only — the email should still feel very similar to the original.`;
    if (level <= 6) return `Take the existing email content and improve it noticeably. Rephrase sentences for better flow, enhance professionalism, and make it more engaging. Keep the core message and intent, but feel free to restructure sentences and improve the overall quality significantly.`;
    if (level <= 8) return `Take the existing email content and substantially rewrite it. Use the same key information and intent, but create a fresh, compelling version with new phrasing, better structure, and a more engaging approach. The result should feel like a new email covering the same points.`;
    return `Use the existing email only as a source of information (recipient, topic, intent). Write a completely new, creative email from scratch based on these details. New structure, new angle, new wording — make it stand out and feel entirely fresh.`;
  };

  const handlePerfectCurrentEmail = async (creativityLevel) => {
    if (!currentBulkContact || isPerfecting || !bodyContent || bodyContent.trim() === "") return;
    
    const contactId = currentBulkContact.id;
    const originalBody = bodyContent;
    const level = creativityLevel ?? perfectCreativity;
    setIsPerfecting(true);
    setBodyContent('');
    
    try {
      const currentTemplate = findTopicInUserTopics(userTopics, selectedTopic);
      const model = currentTemplate?.model || aiSettings.model || 'gpt-4.1-mini';
      const maxTokens = currentTemplate?.maxTokens || parseInt(aiSettings.max_tokens || aiSettings.maxTokens) || 500;
      const language = currentTemplate?.language || aiSettings.language || 'auto';
      
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: "Perfect Email",
          context: `Current email content: ${originalBody}`,
          businessName: currentBulkContact.business_name || '',
          firstName: getFirstDmName(currentBulkContact),
          senderName: userSettings.user_name || '',
          senderEmail: selectedSenderEmail,
          textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)),
          topicSpecificNote: getPerfectPrompt(level),
          model,
          maxTokens,
          language,
          perfectMode: true
        })
      });
      
      if (!response.ok) throw new Error(`API-Fehler: ${response.status} ${response.statusText}`);
      
      const data = await response.json();
      if (!data.emailContent) throw new Error('Unerwartetes API-Antwortformat');
      
      if (expectedContactIdRef.current !== contactId) return;
      setBodyContent(data.emailContent);
      setUserEditedBody(true);
    } catch (error) {
      console.error('📧 [BULK-PERFECT] Error perfecting email:', error);
      if (expectedContactIdRef.current === contactId) {
        setBodyContent(originalBody);
      }
    } finally {
      setIsPerfecting(false);
    }
  };

  const fetchWritingSparks = async (emailText, contact, directionPrompt, category, position = 'top') => {
    if (sparksLockRef.current) return;
    sparksLockRef.current = true;
    setSparksLoading(true);
    setSparksLoadingPos(writingSparks.length > 0 ? position : null);
    let creditDeducted = false;
    try {
      if (sparksGenCount > 0) {
        try {
          const deductCreditsFn = httpsCallable(functions, 'deductCredits');
          await deductCreditsFn({
            action: 'salesgod_writer',
            amount: costOf('emailSalesgodWriter'),
            worldId: userId,
            metadata: { type: directionPrompt ? 'direction' : category ? 'category' : 'refresh', category: category || null, contact: (contact || currentBulkContact)?.business_name || '' },
          });
          creditDeducted = true;
        } catch (creditErr) {
          console.error('Credit deduction failed:', creditErr);
          setSparksLoading(false);
          setSparksLoadingPos(null);
          sparksLockRef.current = false;
          alert('Nicht genügend Credits. Bitte lade deine Credits auf.');
          return;
        }
      }
      setSparksGenCount(prev => prev + 1);

      const c = contact || currentBulkContact;
      const igInsight = c?.researchIntelligence?.instagramInsight;
      const igPosts = c?.researchIntelligence?.instagramPosts;
      const ri = c?.researchIntelligence || {};

      const currentTemplate = findTopicInUserTopics(userTopics, selectedTopic);

      const descClean = igInsight
        ? (c?.lead_description || '').replace(/\n?\[IG Research\].*$/s, '').trim()
        : (c?.lead_description || '');

      const igContextParts = [];
      if (igInsight) {
        igContextParts.push(`[IG RESEARCH] Instagram (@${igInsight.handle}): ${igInsight.summary}`);
        if (igInsight.uniqueFacts?.length) igContextParts.push(`Key facts: ${igInsight.uniqueFacts.join('; ')}`);
        if (igInsight.topics?.length) igContextParts.push(`Their topics: ${igInsight.topics.join(', ')}`);
        if (igInsight.targetAudience) igContextParts.push(`Target audience: ${igInsight.targetAudience}`);
        if (igInsight.painPoints?.length) igContextParts.push(`Pain points: ${igInsight.painPoints.join('; ')}`);
        if (igInsight.sellingPoints?.length) igContextParts.push(`Their USPs: ${igInsight.sellingPoints.join('; ')}`);
        if (igInsight.salesAngle) igContextParts.push(`Sales angle: ${igInsight.salesAngle}`);
        if (igInsight.activityScore != null) igContextParts.push(`Activity: ${igInsight.activityScore}/10, Engagement: ${igInsight.engagementScore ?? '?'}/10${igInsight.engagementTrend && igInsight.engagementTrend !== 'stable' ? ` (${igInsight.engagementTrend})` : ''}`);
        if (Array.isArray(igPosts) && igPosts.length > 0) {
          const recentPosts = igPosts.filter(p => p.text).slice(0, 3).map((p, i) => {
            const excerpt = p.text.length > 120 ? p.text.substring(0, 120) + '...' : p.text;
            const stats = [p.like_count ? `${p.like_count} likes` : '', p.comment_count ? `${p.comment_count} comments` : ''].filter(Boolean).join(', ');
            return `  ${i + 1}. "${excerpt}"${stats ? ` (${stats})` : ''}`;
          });
          if (recentPosts.length > 0) igContextParts.push(`Recent posts:\n${recentPosts.join('\n')}`);
        }
      }

      const sparkSeo = ri.seoAnalysis;
      const seoContextParts = [];
      if (sparkSeo && sparkSeo.data) {
        const core = sparkSeo.data.core;
        const head = [];
        if (core?.visibility?.value != null) head.push(`Visibility ${Number(core.visibility.value).toFixed(2)}`);
        if (core?.keywordsTotal != null) head.push(`${core.keywordsTotal} keywords`);
        if (core?.keywordsTop10 != null) head.push(`${core.keywordsTop10} in Top 10`);
        if (head.length) seoContextParts.push(`[SEO ANALYSIS] ${head.join(' · ')}`);
        const tv = sparkSeo.data.trafficValue;
        if (tv?.estimatedTrafficMonthly != null) seoContextParts.push(`Estimated organic traffic: ~${tv.estimatedTrafficMonthly.toLocaleString('de-DE')} visitors/month${tv.estimatedValueMonthly ? ` worth ~€${tv.estimatedValueMonthly.toLocaleString('de-DE')}/month` : ''}`);
        const vt = sparkSeo.data.visibilityTrend;
        if (vt?.changePct != null) seoContextParts.push(`Visibility trend: ${vt.direction === 'up' ? 'GAINING' : vt.direction === 'down' ? 'LOSING' : 'flat'} ${vt.changePct > 0 ? '+' : ''}${vt.changePct}% over ${vt.overWindow || '12M'}`);
        if (sparkSeo.data.backlinks?.total != null) seoContextParts.push(`Backlinks: ${sparkSeo.data.backlinks.total}`);
        const comps = Array.isArray(sparkSeo.data.competitors) ? sparkSeo.data.competitors.slice(0, 2) : [];
        if (comps.length) seoContextParts.push(`Top competitors: ${comps.map(x => x.domain).join(', ')}`);
        const opps = Array.isArray(sparkSeo.data.opportunities) ? sparkSeo.data.opportunities.slice(0, 2) : [];
        if (opps.length) seoContextParts.push(`Quick wins: ${opps.map(o => `"${o.keyword}" (pos ${o.position})`).join(', ')}`);
      }

      const richContext = [
        descClean,
        c?.lead_city ? `City: ${c.lead_city}` : '',
        c?.lead_type ? `Type: ${c.lead_type}` : '',
        c?.lead_website ? `Website: ${c.lead_website}` : '',
        ri.companyInfo ? `Company intel: ${ri.companyInfo}` : '',
        ri.industryFit ? `Industry fit: ${ri.industryFit}` : '',
        ri.leadScore ? `Lead score: ${ri.leadScore} (${ri.leadRating || ''})` : '',
        ri.budgetIndicators ? `Budget indicators: ${ri.budgetIndicators}` : '',
        ri.reasoning ? `Research reasoning: ${ri.reasoning}` : '',
        ri.companySizeEstimate ? `Company size: ${ri.companySizeEstimate}` : '',
        c?.lead_instagram_followers ? `Instagram followers: ${c.lead_instagram_followers}` : '',
        ...igContextParts,
        ...seoContextParts,
        c?.lead_crawl_summary ? `Website summary: ${c.lead_crawl_summary}` : '',
      ].filter(Boolean).join('\n');

      const templateNote = currentTemplate?.topicSpecificNote
        || (currentTemplate?.topicSpecificNotes?.[0]?.text)
        || '';

      // CLONE-AWARE: feed the voice-clone template's campaign brief (stored in
      // cloneConfig, not generalInfo) to the Writer + pass cloneId so the backend folds
      // in the trained voice + taboos.
      const isCloneTemplate = currentTemplate?.mode === 'voiceClone';
      const cloneBrief = isCloneTemplate ? buildCloneWriterBrief(currentTemplate.cloneConfig) : '';
      const effectiveGeneralInfo = isCloneTemplate
        ? [currentTemplate?.generalInfo || '', cloneBrief].filter(Boolean).join('\n')
        : (currentTemplate?.generalInfo || '');

      // Digest reuse: build the rich digest ONCE per (context + email + offer). If the
      // hash still matches, reuse the cached digest (lean credits) and push the ledger of
      // already-used angles so each generation picks FRESH angles.
      const digestHash = hashWriterContext(richContext, emailText || '', effectiveGeneralInfo, templateNote || '', isCloneTemplate ? (currentTemplate?.cloneConfig?.cloneId || '') : '');
      const hashMatches = digestHashRef.current === digestHash;
      const reuseDigest = hashMatches && cachedBriefing && cachedBriefing.angleInventory;
      if (!hashMatches) {
        usedAnglesRef.current = [];
      }

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateWritingSparks(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: c?.business_name || '',
          website: c?.lead_website || '',
          firstName: getFirstDmName(c),
          recipientName: '',
          recipientRole: '',
          senderName: userSettings.user_name || '',
          context: richContext,
          currentEmailText: emailText || '',
          topic: selectedTopic || '',
          language: currentTemplate?.language || aiSettings.language || 'auto',
          generalInfo: effectiveGeneralInfo,
          templateNote,
          companyName: userSettings.company_name || '',
          direction: directionPrompt || null,
          pacing: c?.lead_pacing || null,
          cachedBriefing: reuseDigest ? cachedBriefing : null,
          usedAngles: usedAnglesRef.current,
          category: category || null,
          persona: activePersona || null,
          cloneId: isCloneTemplate ? (currentTemplate?.cloneConfig?.cloneId || null) : null,
          userId: userId || null,
        })
      });
      const data = await response.json();
      const icebreakerSparks = (data.icebreakers || []).map((s, i) => ({ ...s, type: 'icebreaker', batch: 0, colorIdx: i % 4 }));
      const regularSparks = (data.sparks || []).map((s, i) => ({ ...s, colorIdx: i % 4, categoryTag: category || null }));
      const allNewSparks = [...icebreakerSparks, ...regularSparks];
      if (allNewSparks.length > 0) {
        if (writingSparks.length > 0) {
          const dirLabel = directionPrompt ? (writerDirections.find(d => d.prompt === directionPrompt)?.label || directionPrompt) : null;
          const catLabel = category ? WRITER_CATEGORIES.find(c => c.id === category)?.label : null;
          const taggedNew = allNewSparks.map(s => ({ ...s, directionLabel: dirLabel || catLabel || null }));
          if (position === 'bottom') {
            setWritingSparks(prev => [...prev, ...taggedNew]);
          } else {
            const taggedOld = writingSparks.map(s => s.previous ? s : { ...s, previous: true });
            setWritingSparks([...taggedNew, ...taggedOld]);
          }
        } else {
          setWritingSparks(allNewSparks);
        }
      }
      if (data.briefing) {
        setCachedBriefing(data.briefing);
        digestHashRef.current = digestHash;
      }
      const newAngleIds = allNewSparks.map(s => s.angleId).filter(Boolean);
      if (newAngleIds.length > 0) {
        usedAnglesRef.current = Array.from(new Set([...usedAnglesRef.current, ...newAngleIds]));
      }
      if (data.directions?.length && !directionPrompt) setWriterDirections(data.directions);
    } catch (err) {
      console.error('SalesGod Writer error:', err);
      if (creditDeducted) {
        try {
          const deductCreditsFn = httpsCallable(functions, 'deductCredits');
          await deductCreditsFn({ action: 'salesgod_writer_refund', amount: -costOf('emailSalesgodWriter'), worldId: userId });
        } catch (_) { /* best effort */ }
      }
    } finally {
      setSparksLoading(false);
      setSparksLoadingPos(null);
      sparksLockRef.current = false;
    }
  };

  const expandSpark = async (index) => {
    if (expandLockRef.current) return;
    const parentSpark = writingSparks[index];
    if (!parentSpark) return;
    expandLockRef.current = true;
    let creditDeducted = false;
    if (sparksGenCount > 0) {
      try {
        const deductCreditsFn = httpsCallable(functions, 'deductCredits');
        await deductCreditsFn({
          action: 'salesgod_writer_expand',
          amount: costOf('emailSalesgodExpand'),
          worldId: userId,
          metadata: { contact: currentBulkContact?.business_name || '' },
        });
        creditDeducted = true;
      } catch (creditErr) {
        console.error('Credit deduction failed:', creditErr);
        expandLockRef.current = false;
        alert('Nicht genügend Credits. Bitte lade deine Credits auf.');
        return;
      }
    }
    setSparksGenCount(prev => prev + 1);
    setExpandingIdx(index);
    try {
      const currentTemplate = findTopicInUserTopics(userTopics, selectedTopic);
      const templateNote = currentTemplate?.topicSpecificNote
        || (currentTemplate?.topicSpecificNotes?.[0]?.text)
        || '';
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateWritingSparks(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expandFromSentence: parentSpark.text,
          language: currentTemplate?.language || aiSettings.language || 'auto',
          currentEmailText: bodyContent || '',
          templateNote,
          generalInfo: currentTemplate?.generalInfo || '',
          persona: activePersona || null,
        })
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      if (data.sparks?.length) {
        const newCards = data.sparks.map(s => ({ ...s, expanded: true, colorIdx: parentSpark.colorIdx, ...(parentSpark.previous && { previous: true }) }));
        setWritingSparks(prev => {
          const copy = [...prev];
          copy.splice(index + 1, 0, ...newCards);
          return copy;
        });
      } else {
        setSparksCopied('__expand_failed__');
        setTimeout(() => setSparksCopied(null), 2000);
      }
    } catch (err) {
      console.error('SalesGod Writer expand error:', err);
      setSparksCopied('__expand_failed__');
      setTimeout(() => setSparksCopied(null), 2000);
      if (creditDeducted) {
        try {
          const deductCreditsFn = httpsCallable(functions, 'deductCredits');
          await deductCreditsFn({ action: 'salesgod_writer_refund', amount: -costOf('emailSalesgodExpand'), worldId: userId });
        } catch (_) { /* best effort */ }
      }
    } finally {
      setExpandingIdx(null);
      expandLockRef.current = false;
    }
  };

  const bulkContactId = currentBulkContact?.id || currentBulkContact?.lead_id || currentBulkContact?.business_name;
  useEffect(() => {
    setWritingSparks([]);
    setWriterDirections([]);
    setActiveDirection(null);
    setActiveCategory(null);
    setCachedBriefing(null);
    setSparksGenCount(0);
    setSparksCopied(null);
    setExpandingIdx(null);
    usedAnglesRef.current = [];
    digestHashRef.current = null;
  }, [bulkContactId, selectedTopic]);

  useEffect(() => {
    if (sidePanelOpen && bodyContent && writingSparks.length === 0 && !sparksLoading) {
      fetchWritingSparks(bodyContent);
    }
  }, [sidePanelOpen, bodyContent]);

  // Generiere Bulk-E-Mails für alle Kontakte beim Start
  const generateAllBulkEmails = async () => {
    if (updatedBulkContacts.length === 0) return;

    // Bulk generation kicks off automatically when the composer opens, so an
    // empty balance would otherwise show up as a stalled progress bar. Stop
    // before the first API call and explain instead.
    if (!requireCredits(emailGenerationCost({}))) {
      setIsBulkGenerating(false);
      setCreditError({ exhausted: true, available: creditBalance || 0, total: updatedBulkContacts.length });
      return;
    }

    setCreditError(null);

    // --- Batch credit deduction: one call instead of per-email ---
    // Worst case across every template that could be picked for a contact.
    // Priced exactly like the per-email tracking below, so the batch charge is
    // always >= the real total and the difference comes back as a refund.
    // Voice-Clone tiers MUST be part of this: Ultra costs ~60x a standard mail,
    // and a batch that only estimated the standard price would hand those out
    // essentially for free (per-email deduction is skipped once the batch is in).
    const templateCost = (t) => emailGenerationCost({
      hyperIntelligence: !!t?.hyperIntelligence,
      hasReferenceFile: !!t?.referenceFile?.claudeFileId,
      voiceCloneTier: t?.mode === 'voiceClone' ? (t?.cloneConfig?.qualityTier || 'best') : null,
    });
    const candidateCosts = Object.values(userTopics || {}).map(templateCost);
    const maxCostPerEmail = candidateCosts.length
      ? Math.max(...candidateCosts)
      : emailGenerationCost({});
    const totalMaxCost = updatedBulkContacts.length * maxCostPerEmail;
    let batchDeducted = 0;
    let batchDeductionOk = false;

    try {
      const deductCreditsFn = httpsCallable(functions, 'deductCredits');
      const res = await deductCreditsFn({
        action: 'bulk_email_batch',
        amount: totalMaxCost,
        worldId: userId,
        metadata: { contactCount: updatedBulkContacts.length, maxCostPerEmail },
      });
      batchDeducted = totalMaxCost;
      batchDeductionOk = true;
      console.log('📧 [BULK-CREDITS] Batch deducted', totalMaxCost, 'credits. New balance:', res.data?.newBalance);
    } catch (batchErr) {
      // Worst case does not fit the balance — fall back to the cheapest real
      // price so a nearly empty account can still run a standard batch. Anything
      // more expensive is settled up after generation (see settleBatchCredits).
      console.warn('📧 [BULK-CREDITS] Worst-case deduction failed, trying base cost:', batchErr.message);
      try {
        const baseCostPerEmail = emailGenerationCost({});
        const baseCost = updatedBulkContacts.length * baseCostPerEmail;
        const deductCreditsFn = httpsCallable(functions, 'deductCredits');
        const res2 = await deductCreditsFn({
          action: 'bulk_email_batch',
          amount: baseCost,
          worldId: userId,
          metadata: { contactCount: updatedBulkContacts.length, costPerEmail: baseCostPerEmail },
        });
        batchDeducted = baseCost;
        batchDeductionOk = true;
        console.log('📧 [BULK-CREDITS] Base-cost batch deducted', baseCost, 'credits. New balance:', res2.data?.newBalance);
      } catch (baseErr) {
        console.error('📧 [BULK-CREDITS] Batch deduction failed completely:', baseErr.message);
        setCreditError({
          exhausted: true,
          available: creditBalance || 0,
          total: updatedBulkContacts.length,
        });
        // Only claim "out of credits" when that is what actually happened. This
        // catch also covers a rate-limited or cold-started callable, and telling
        // a well-funded account it is broke would send them chasing a
        // non-existent problem.
        const reallyOutOfCredits = /not enough credits/i.test(baseErr?.message || '')
          || baseErr?.code === 'resource-exhausted'
          || /resource-exhausted/.test(baseErr?.code || '');
        if (reallyOutOfCredits) {
          openCreditsModal(emailGenerationCost({}) * updatedBulkContacts.length);
        }
      }
    }

    setIsBulkGenerating(true);
    setBulkGenerationProgress(0);
    
    const generatedEmails = {};
    let progress = 0;
    let actualCreditCost = 0;

    // Settle the up-front batch charge against what was actually generated.
    // Normally a refund (the batch is charged at the worst case), but when only
    // the base-cost fallback fit the balance the shortfall is collected here —
    // otherwise expensive templates would be generated at the cheap price.
    const settleBatchCredits = async () => {
      if (!batchDeductionOk || actualCreditCost <= 0) return;
      const delta = Math.round((actualCreditCost - batchDeducted) * 100) / 100;
      if (delta === 0) return;
      const isRefund = delta < 0;
      try {
        const deductCreditsFn = httpsCallable(functions, 'deductCredits');
        await deductCreditsFn({
          action: isRefund ? 'bulk_email_refund' : 'bulk_email_topup',
          amount: delta, // negative refunds, positive charges the difference
          worldId: userId,
          metadata: { batchDeducted, actualCreditCost },
        });
        console.log(
          `📧 [BULK-CREDITS] ${isRefund ? 'Refunded' : 'Charged'} ${Math.abs(delta)} credits`
          + ` (deducted: ${batchDeducted}, actual: ${actualCreditCost})`
        );
      } catch (settleErr) {
        console.warn('📧 [BULK-CREDITS] Settlement failed:', settleErr.message);
      }
    };
    
    // Reduce initial pre-load when any active template uses HyperIntelligence
    const hasHyperIntelligence = Object.values(userTopics).some(t => t.hyperIntelligence === true);
    const initialLoadCount = Math.min(hasHyperIntelligence ? 2 : 5, updatedBulkContacts.length);
    const initialContactIds = updatedBulkContacts.slice(0, initialLoadCount);
    const remainingContactIds = updatedBulkContacts.slice(initialLoadCount);
    
    // Helper: generate one email, track cost
    const generateOne = async (contactId) => {
      const contact = allData.find(row => row.id === contactId);
      if (!contact) return null;

      try {
        const contactTopic = await determineTopicForContact(contact);
        console.log('📧 [BULK-GENERATION] Contact:', contact.business_name, 'Topic:', contactTopic);

        const cfCtx = (customContextFields || [])
          .map(cf => { const v = contact[cf.key]; return v ? `${cf.label}: ${v}` : ''; })
          .filter(Boolean).join('\n');
        const preferredAddr = activeTemplatePref
          ? (resolvePreferredEmail(contact, activeTemplatePref, { dmFallback: activeDmFallback })?.email || null)
          : null;
        const emailContent = await generateEmailForContact(
          contact,
          contactTopic,
          `
Business Name: ${contact.business_name || ''}
City: ${contact.lead_city || ''}
Description: ${contact.lead_description || ''}
Type: ${contact.lead_type || ''}
Website: ${contact.lead_website || ''}${cfCtx ? `\n${cfCtx}` : ''}
          `.trim(),
          userId,
          userSettings,
          aiSettings,
          userTopics,
          topicTypeMapping,
          getDefaultTopicNote,
          customContextFields,
          getRecipientInfoForContact(contact, preferredAddr),
          null,
          { skipCreditDeduction: batchDeductionOk, textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)) }
        );

        // Track the real cost per email so the batch charge can be settled.
        // Voice-Clone-mode templates bill per quality tier — the tier spread is
        // large (Good vs Ultra is ~7x), so it has to be priced per template
        // rather than as one flat per-email figure.
        const template = findTopicInUserTopics(userTopics, contactTopic);
        actualCreditCost += emailGenerationCost({
          hyperIntelligence: !!template?.hyperIntelligence,
          hasReferenceFile: !!template?.referenceFile?.claudeFileId,
          voiceCloneTier:
            template?.mode === 'voiceClone' ? (template?.cloneConfig?.qualityTier || 'best') : null,
        });

        return emailContent;
      } catch (error) {
        console.error(`Error generating email for contact ${contactId}:`, error);
        return {
          error: true,
          creditError: (error.message || '').includes('Not enough credits'),
          message: error.message,
          subject: getTopicSubjectLine(contact),
        };
      }
    };

    // Zuerst die ersten 5 Kontakte laden
    for (const contactId of initialContactIds) {
      const result = await generateOne(contactId);
      if (result) generatedEmails[contactId] = result;

      progress = Math.round(((Object.keys(generatedEmails).length) / updatedBulkContacts.length) * 100);
      setBulkGenerationProgress(progress);
    }
    
    // Lade die erste E-Mail, sobald die ersten 5 verfügbar sind
    if (updatedBulkContacts.length > 0 && currentBulkContact) {
      const generatedEmail = generatedEmails[currentBulkContact.id];
      if (generatedEmail && !generatedEmail.error) {
        updateSubjectInputDirectly(generatedEmail.subject || "");
        setBodyContent(generatedEmail.body || "");
      }
    }
    
    // Progress auf 100% setzen — die Animation in BulkGenerationLoading
    // übernimmt den Übergang und ruft onTransitionComplete auf
    setBulkGenerationProgress(100);
    
    // Jetzt den Rest im Hintergrund laden
    if (remainingContactIds.length > 0) {
      (async () => {
        for (const contactId of remainingContactIds) {
          const result = await generateOne(contactId);
          if (result) {
            setBulkGeneratedEmails(prev => ({ ...prev, [contactId]: result }));
          }

          const totalProcessed = initialLoadCount + remainingContactIds.indexOf(contactId) + 1;
          const newProgress = Math.round((totalProcessed / updatedBulkContacts.length) * 100);
          setBulkGenerationProgress(newProgress);
        }
        
        setBulkGenerationComplete(true);

        await settleBatchCredits();
      })();
    } else {
      setBulkGenerationComplete(true);

      await settleBatchCredits();
    }
    
    // Speichere die ersten generierten E-Mails im State
    setBulkGeneratedEmails(generatedEmails);
  };

  // Modifizierte onClose-Funktion für Statistik-Toast
  const handleClose = () => {
    // Nimm die Werte aus den Refs für die aktuellsten Zahlen
    const sent = sessionSentRef.current;
    const skipped = sessionSkippedRef.current;
    const deleted = sessionDeletedRef.current;
    
    // Zeige Toast mit Statistiken
    if (sent > 0 || skipped > 0 || deleted > 0) {
      // Formatiere eine aussagekräftige Nachricht auf Englisch
      let message = "Bulk session summary: ";
      const parts = [];
      
      // Stelle die gelöschten Kontakte zuerst dar, wenn vorhanden
      if (deleted > 0) {
        parts.push(`${deleted} contact${deleted === 1 ? '' : 's'} deleted`);
      }
      
      if (sent > 0) {
        parts.push(`${sent} email${sent === 1 ? '' : 's'} sent`);
      }
      
      if (skipped > 0) {
        parts.push(`${skipped} skipped`);
      }
      
      // Wenn keine E-Mails gesendet wurden, aber Kontakte gelöscht wurden
      if (sent === 0 && deleted > 0) {
        message = "Contacts deleted: " + deleted;
      } else {
        message += parts.join(', ');
      }
      
      // Zeige den Toast an
      if (onEmailResult) {
        onEmailResult('summary', message);
      }
    }
    
    // Statistiken zurücksetzen, damit alte gesendete E-Mails nicht bei späteren Löschungen angezeigt werden
    sessionSentRef.current = 0;
    sessionSkippedRef.current = 0;
    sessionDeletedRef.current = 0;
    setSessionStats({
      sent: 0,
      skipped: 0,
      deleted: 0
    });
    
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    
    // Originale onClose-Funktion aufrufen
    onClose();
  };

  // Kontakt in Firestore löschen
  const handleDeleteContact = async (contactId) => {
    if (!userId || !contactId || deletingRef.current || isSending) return;
    deletingRef.current = true;

    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    
    try {
      // Speichere den aktuellen Index vor dem Löschen
      const currentPos = currentBulkIndex;
      
      // Kontakt in Firestore löschen
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);
      await deleteDoc(contactDocRef);

      // Discarding a contact we hold a prepared draft for has to take the draft
      // with it, or the Prepared Mails strip keeps a card whose contact is gone.
      // A backend trigger catches this too, but doing it here makes the card
      // disappear immediately instead of a round-trip later.
      if (preparedContactsRef.current.has(contactId)) {
        preparedContactsRef.current.delete(contactId);
        deleteDoc(doc(firestore, `World/${userId}/preparedEmails/${contactId}`)).catch(() => {});
      }
      
      // Lösch-Event an parent, aber nur ID String -> keine Toast-Fehler
      if (onEmailResult) {
        onEmailResult('contact-deleted', String(contactId));
      }
      
      // Erhöhe den Ref zuerst, dann aktualisiere den State
      sessionDeletedRef.current += 1;
      setSessionStats(prev => ({
        ...prev,
        deleted: sessionDeletedRef.current
      }));
      
      const updatedContacts = updatedBulkContacts.filter(id => id !== contactId);
      setUpdatedBulkContacts(updatedContacts);
      
      setBodyContent("");
      updateSubjectInputDirectly("");
      setScreenComposerImage(null);
      setSendSuccess(false);
      setIsSending(false);
      setUserEditedSubject(false);
      setUserEditedBody(false);
      
      if (updatedContacts.length === 0) {
        handleClose();
        return;
      }
      
      // Prüfe, ob alle verbliebenen Kontakte bereits gesendet wurden
      const allRemainingSent = updatedContacts.every(id => isContactSent(id));
      
      // Wenn alle verbleibenden Kontakte bereits eine E-Mail haben, schließe den Modal
      if (allRemainingSent) {
        console.log("Alle verbliebenen Kontakte wurden bereits bearbeitet, schließe Modal");
        handleClose();
        return;
      }
      
      // Find nearest unsent contact: search forward from current position, then backward
      const clampedPos = Math.min(currentPos, updatedContacts.length - 1);
      let nextUnsent = -1;
      for (let i = clampedPos; i < updatedContacts.length; i++) {
        if (!isContactSent(updatedContacts[i])) { nextUnsent = i; break; }
      }
      if (nextUnsent === -1) {
        for (let i = clampedPos - 1; i >= 0; i--) {
          if (!isContactSent(updatedContacts[i])) { nextUnsent = i; break; }
        }
      }
      
      if (nextUnsent !== -1) {
        expectedContactIdRef.current = updatedContacts[nextUnsent];
        setCurrentBulkIndex(nextUnsent);
      }
      
    } catch (error) {
      console.error("Error deleting contact:", error);
    } finally {
      deletingRef.current = false;
    }
  };

  const isContactSent = (contactId) => sentEmails[contactId] || sentContactsRef.current.has(contactId);

  const findNextUnsentIndex = (startAfter) => {
    for (let i = startAfter + 1; i < updatedBulkContacts.length; i++) {
      if (!isContactSent(updatedBulkContacts[i])) return i;
    }
    return -1;
  };

  const findPreviousUnsentIndex = (startBefore) => {
    for (let i = startBefore - 1; i >= 0; i--) {
      if (!isContactSent(updatedBulkContacts[i])) return i;
    }
    return -1;
  };

  const navigateToBulkIndex = (targetIndex) => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    setSendSuccess(false);
    setIsSending(false);
    setUserEditedSubject(false);
    setUserEditedBody(false);
    setScreenComposerImage(null);
    setBodyContent("");
    updateSubjectInputDirectly("");
    expectedContactIdRef.current = updatedBulkContacts[targetIndex];
    setCurrentBulkIndex(targetIndex);
  };

  const goToNextBulkContact = () => {
    const nextIndex = findNextUnsentIndex(currentBulkIndex);
    if (nextIndex !== -1) navigateToBulkIndex(nextIndex);
  };
  
  const goToPreviousBulkContact = () => {
    const prevIndex = findPreviousUnsentIndex(currentBulkIndex);
    if (prevIndex !== -1) navigateToBulkIndex(prevIndex);
  };
  
  const canGoToPrevious = () => findPreviousUnsentIndex(currentBulkIndex) !== -1;
  const canGoToNext = () => findNextUnsentIndex(currentBulkIndex) !== -1;

  // Direktes Aktualisieren des Betreffs
  const updateSubjectInputDirectly = (subject) => {
    setSubjectLine(subject);
    
    if (subjectInputRef.current) {
      subjectInputRef.current.value = subject;
      
      const event = new Event('input', { bubbles: true });
      subjectInputRef.current.dispatchEvent(event);
    }
  };

  // E-Mail senden und sofort zur nächsten weitergehen
  const handleSend = async () => {
    if (!currentBulkContact || !selectedEmail) {
      console.error("No email address found for this contact");
      onEmailResult('error', 'No email address found for this contact');
      return;
    }

    // ⛔ Image guard: don't send while this contact's AI image is still
    // generating (covers keyboard shortcuts that bypass the disabled button).
    if (Object.values(bulkAiImages[currentBulkContact.id] || {}).some((v) => v && v.status === "loading")) {
      onEmailResult('error', 'Please wait — an AI image is still being generated.');
      return;
    }

    const currentId = currentBulkContact.id;

    // ⛔ Undeliverable guard: this exact address hard-bounced before. Blocked
    // client-side (mirrors the server gate) so a stale auto-selection can't slip
    // through. Other addresses of the contact stay usable.
    if (isEmailUndeliverable(selectedEmail)) {
      onEmailResult('error', 'This address previously bounced (undeliverable) and cannot be emailed. Pick another address.');
      return;
    }

    // 🔴 Re-send guard: this exact address already received an intro/follow-up.
    // Demand an explicit extra confirmation before mailing it a second time.
    // A different, not-yet-contacted address on the same domain stays free to send.
    if (!resendConfirmedRef.current && isResendBlocked(selectedEmail)) {
      setResendConfirmOpen(true);
      return;
    }
    resendConfirmedRef.current = false;

    // 🟡 Same-step guard: an intro (or an earlier follow-up) is expected.
    // Only this stage, or this template, already having gone to the address
    // asks for a confirmation. A first follow-up after the intro does not.
    if (
      !followUpConfirmedRef.current
      && followUpDuplicates.length > 0
    ) {
      setFollowUpConfirmOpen(true);
      return;
    }
    followUpConfirmedRef.current = false;

    if (sentContactsRef.current.has(currentId)) {
      console.warn('⚠️ [BULK-SEND] Blocked double-send for contact:', currentId);
      return;
    }
    sentContactsRef.current.add(currentId);

    const currentContent = bodyContent;
    const currentSubject = subjectLine;
    const currentSelectedEmail = selectedEmail;
    const currentCcEmails = [...ccEmails];
    const currentBusinessName = currentBulkContact.business_name || currentBulkContact.lead_name || 'Unbekannt';
    const currentTopic = selectedTopic;
    const currentPreparedMeta = manualTopicForRef.current.has(currentId)
      ? null
      : preparedMetaRef.current[currentId];
    const currentEmailType = currentPreparedMeta?.emailType || topicTypeMapping[currentTopic] || "";

    let currentSenderEmail = selectedSenderEmail;

    const preparedMail = bulkGeneratedEmails[currentId];
    const preparedAccount = preparedMail?._prepared
      ? pickPreparedSenderAccount({
          senderId: preparedMail._preparedSenderId,
          senderEmail: preparedMail._preparedSenderEmail,
        }, senderEmails)
      : null;

    if (preparedAccount) {
      // Signature was generated for this inbox; footer + SMTP must follow it
      // unless the user picked a different From on this card.
      currentSenderEmail = resolvePreparedSendEmail({
        preparedAccount,
        selectedSenderEmail,
        randomSenderMode,
      });
    } else {
      // Flow override: if this contact is on a flow "Send Email" step with a
      // non-default sender mode, resolve (and persist) the flow sender per contact.
      let flowSenderApplied = false;
      try {
        const flowStep = resolveFlowEmailStep(currentBulkContact, warmupFlows, leadFlows);
        if (flowStep && flowStep.senderMode && flowStep.senderMode !== 'default') {
          const flowEmail = await resolveFlowSenderEmail({
            userId,
            contactId: currentId,
            flowConfig: {
              senderMode: flowStep.senderMode,
              senderId: flowStep.senderId,
              senderPool: flowStep.senderPool,
              flowSenderId: flowStep.flowSenderId,
              flowId: flowStep.flowId,
              flowField: flowStep.flowField,
            },
            senderEmails,
          });
          if (flowEmail) {
            currentSenderEmail = flowEmail;
            flowSenderApplied = true;
          }
        }
      } catch (err) {
        console.error('[BULK-SEND] Flow sender resolution failed:', err);
      }

      if (!flowSenderApplied && randomSenderMode && senderEmails.length > 1) {
        const others = senderEmails.filter(e => e.email !== selectedSenderEmail);
        const pick = others[Math.floor(Math.random() * others.length)];
        currentSenderEmail = pick.email;
        setSelectedSenderEmail(pick.email);
      }
    }

    // 📧 Tracking-Daten ermitteln - finde Template-ID, Subject Line ID und Instruction ID
    const trackingInfo = getTrackingInfoForContact(currentBulkContact, currentTopic);

    setIsSending(true);
    
    // Bestimme den neuen Kontaktstatus
    let newStatus = null;
    if (selectedTopic === "Introduce Yourself") {
      newStatus = "Introduced";
    } else if (selectedTopic === "Follow Up") {
      newStatus = "First Follow-up";
    } else if (selectedTopic === "Second Follow Up") {
      newStatus = "Second Follow-up";
    } else if (selectedTopic === "Third Follow Up") {
      newStatus = "Third Follow-up";
    }
    
    // Optimistische UI-Aktualisierung
    setSendSuccess(true);
    
    // Statistik für gesendete E-Mails erhöhen (sowohl im State als auch im Ref)
    sessionSentRef.current += 1;
    setSessionStats(prev => ({
      ...prev,
      sent: sessionSentRef.current
    }));
    
    // Markiere diese E-Mail als gesendet
    setSentEmails(prev => ({
      ...prev,
      [currentId]: {
        timestamp: Date.now(),
        status: newStatus
      }
    }));
    
    // sentContactsRef is already updated synchronously above, so isContactSent is accurate
    const nextUnsentIdx = findNextUnsentIndex(currentBulkIndex);

    pendingSends.current[currentId] = true;

    if (nextUnsentIdx !== -1) {
      autoAdvanceTimerRef.current = setTimeout(() => {
        autoAdvanceTimerRef.current = null;
        navigateToBulkIndex(nextUnsentIdx);
      }, 300);
    } else {
      autoAdvanceTimerRef.current = setTimeout(() => {
        autoAdvanceTimerRef.current = null;
        handleClose();
      }, 1000);
    }
    
    // Aktualisiere den Kontaktstatus
    if (newStatus) {
      updateContactStatus(userId, currentId, newStatus, onEmailResult, selectedEmail, currentBulkContact)
        .catch(error => console.error("Error updating contact status:", error));
    }
    
    // 🔥 NEU: Prüfe und setze AI Tag basierend auf dem verwendeten Topic
    setAiTagAfterEmailSend(currentTopic, currentId)
      .catch(error => console.error("Error setting AI tag after email send:", error));
    
    // 📧 EMAIL TRACKING: Stats werden jetzt im Backend aktualisiert (WorldEmailSender.js)
    // Das verhindert doppeltes Tracking und funktioniert auch für geschedulte E-Mails
    
    if (onEmailResult) {
      onEmailResult('success', `Email to ${currentBusinessName} successfully sent`, currentBulkContact, null, currentSelectedEmail, currentSenderEmail);
    }
    
    // E-Mail im Hintergrund senden (kein await, damit es parallel läuft)
    (async () => {
      try {
        // Absender-E-Mail finden
        const senderEmailData = senderEmails.find(item => item.email === currentSenderEmail);
        
        if (!senderEmailData) {
          throw new Error("The selected sender email was not found.");
        }

        // Resolve gegen stale React-State (Race zwischen Firestore/RTDB-Listenern).
        // Bei Read-Error fällt der Helper transparent auf den bestehenden State zurück.
        const { footerId: resolvedFooterId, footerSettings: resolvedFooterSettings } = await resolveFooterForSend({
          userId,
          senderAccountId: senderEmailData.id,
          fallbackFooterId: senderEmailData.footerId || null,
          fallbackFooterSettings: userSettings,
        });
        
        // Merge this contact's generated AI images so {{image:token}} resolves
        // to the per-recipient image (backend inlines it as CID like any other).
        const contactImages = bulkAiImages[currentId] || {};
        const readyAiImages = Object.fromEntries(
          Object.entries(contactImages).filter(([, v]) => v && v.status === "ready" && v.imageUrl)
        );
        // Resolve {{variables}} in image alt texts per recipient so no two sends
        // share an identical (or placeholder-leaking) alt attribute.
        const settingsForHtml = withResolvedImageAltText(
          withResolvedAiImages(resolvedFooterSettings, readyAiImages),
          buildImagePromptVariables(currentBulkContact, { userSettings, selectedEmail: currentSelectedEmail })
        );

        // E-Mail formatieren mit account-spezifischem Footer
        let formattedContent = convertToHtml(currentContent, settingsForHtml, resolvedFooterId);

        if (screenComposerImage && screenComposerImage.url) {
          formattedContent += `<div style="margin: 20px 0; text-align: center;"><img src="${screenComposerImage.url}" alt="" style="max-width: 100%; border-radius: 8px; display: inline-block;" /></div>`;
        }

        let contentWithFooter = addFooterToEmail(formattedContent, resolvedFooterSettings, resolvedFooterId);
        
        // Request-Daten erstellen
        const requestData = {
          recipients: [currentSelectedEmail, ...currentCcEmails],
          cc: currentCcEmails,
          subject: currentSubject || '',
          content: contentWithFooter || '',
          userId: userId,
          businessName: currentBusinessName,
          leadId: currentId,
          emailType: currentEmailType,
          ...(preparedMail?._safeGuardDraftId ? {
            safeGuardApproved: true,
            attachments: preparedMail._presetAttachments || [],
          } : {}),
          senderEmail: senderEmailData.email.trim(),
          senderPassword: senderEmailData.password,
          senderName: senderEmailData.sender_name || userSettings.user_name || 'BonusClub',
          imapHost: senderEmailData.imap_host,
          imapPort: senderEmailData.imap_port,
          smtpHost: senderEmailData.smtp_host,
          smtpPort: senderEmailData.smtp_port,
          encryptionType: senderEmailData.encryption_type,
          smtpEncryption: String(senderEmailData.smtp_encryption),
          emailId: senderEmailData.id,
          // 📧 EMAIL TRACKING: Send tracking data to backend
          templateId: trackingInfo.templateId,
          subjectLineId: trackingInfo.subjectLineId,
          instructionId: trackingInfo.instructionId,
          // Voice-Clone-mode sends carry a variantId so the backend can credit
          // per-structure-variant stats. Standard sends pass null.
          variantId: trackingInfo.variantId || null,
          topicName: currentTopic,
          // 🔒 AUDIT: Wer hat die E-Mail gesendet (Team-Member-Tracking)
          sentByUserId: currentUserId
        };
        
        // E-Mail senden
        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
        
        const responseText = await response.text();
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Error parsing JSON response:', e);
          if (onEmailResult) {
            onEmailResult('error', `Server response is not valid JSON: ${responseText.substring(0, 100)}...`);
          }
          return;
        }
        
        // Entferne diese E-Mail aus den ausstehenden Sendungen
        delete pendingSends.current[currentId];
        
        if (data.success) {
          // SafeGuard or Pre-Send Scorer rejected → saved as draft
          if (data.drafted) {
            const reason = data.preSendScore
              ? `Pre-Send Score: ${data.preSendScore.compositeScore}/10 — saved as draft`
              : `SafeGuard: ${data.safeGuardRating}/${data.minimumRating || 10} — saved as draft`;
            if (onEmailResult) {
              onEmailResult('warning', reason, currentBulkContact);
            }
            return;
          }

          // Fire-and-forget: couple pre-send score to analytics
          if (data.preSendScore && currentId) {
            coupleScoreToAnalytics(userId, currentId, data.preSendScore);
          }

          // Scheduled emails get their own toast (new info: scheduled time)
          // Normal success is silent — the optimistic toast already confirmed it
          if (onEmailResult && data.delayed && data.scheduledTime) {
            let scheduledTimeStamp = null;
            try {
              scheduledTimeStamp = new Date(data.scheduledTime).getTime();
            } catch (e) {
              console.error("Error converting scheduled time:", e);
            }
            onEmailResult('success', `Email to ${currentBusinessName} scheduled`, currentBulkContact, scheduledTimeStamp, currentSelectedEmail, currentSenderEmail);
          }
          
          if (preparedMail?._safeGuardDraftId) {
            deleteDoc(doc(firestore, 'world_drafts', userId, 'emails', preparedMail._safeGuardDraftId)).catch(() => {});
          }

          // Letzten Absender speichern
          await saveLastSenderToDatabase(currentSenderEmail, userId);

          // This contact had a backend-prepared email waiting, and it just got a
          // mail — drop the draft + the "Prepared" flag so the strip and the
          // "Prepared" tab lose it immediately.
          //
          // The contact's own flag counts, not just the seeded set: a session can
          // hold prepared contacts the seed never recognised (mixed selection), and
          // regenerating the body does not un-prepare the contact — the parked
          // draft is spent either way. The backend repeats this on its side, so a
          // tab closed before the response lands cleans up too; this is only the
          // path that makes the card vanish without a round-trip.
          if (preparedContactsRef.current.has(currentId) || currentBulkContact.has_prepared_email === true) {
            preparedContactsRef.current.delete(currentId);
            deleteDoc(doc(firestore, `World/${userId}/preparedEmails/${currentId}`)).catch(() => {});
            updateDoc(doc(firestore, `World/${userId}/db/${currentId}`), {
              has_prepared_email: false,
              prepared_email_at: deleteField(),
            }).catch(() => {});
          }

          // Auto-advance warmup flow if current step is an email node
          advanceFlowOnEmailSend(userId, currentId).then(flowResult => {
            if (flowResult.advanced && onEmailResult) {
              onEmailResult('flow-update', {
                id: currentId,
                warmupFlow: flowResult.newFlowState,
                flowEndStatus: flowResult.flowEndStatus,
                autoAppliedTags: flowResult.autoAppliedTags,
              });
            }
          });
        } else {
          if (data.optOutBlocked) {
            if (onEmailResult) {
              onEmailResult('blocked', `Contact is blocked or opted out. ${data.error || ''}`, currentBulkContact);
            }
            return;
          }

          // CRM Domain Guard blocked — see NormalModeEmail for full rationale.
          if (data.blocked === true && data.blockType === 'crm_guard') {
            const sourceLabel = data.crmLeadSourceValue
              ? `Lead Source "${data.crmLeadSourceValue}"`
              : (data.crmStatus === 'crm_found_empty_source' ? 'empty Lead Source' : 'foreign Lead Source');
            const crmMessage = `Already in Close CRM (${sourceLabel}) — outreach blocked.`;
            if (onEmailResult) {
              onEmailResult('blocked', crmMessage, currentBulkContact);
            }
            return;
          }

          // Fehler
          let errorMessage = data.error || 'Unknown error sending email';
          
          if (data.error && data.error.includes("Sender address rejected")) {
            errorMessage = `The email address "${senderEmailData.email}" was rejected by the server.`;
          }
          
          if (onEmailResult) {
            onEmailResult('error', errorMessage, currentBulkContact);
          }
        }
      } catch (error) {
        console.error("Error sending email:", error);
        
        if (onEmailResult) {
          onEmailResult('error', error.message || 'Unknown error sending email', currentBulkContact);
        }
        
        // Entferne diese E-Mail aus den ausstehenden Sendungen
        delete pendingSends.current[currentId];
      }
    })();
  };

  // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
  const isEmailValid = () => {
    return (
      selectedEmail && 
      selectedEmail.trim() !== '' && 
      selectedSenderEmail && 
      selectedSenderEmail.trim() !== '' && 
      subjectLine && 
      subjectLine.trim() !== '' && 
      bodyContent && 
      bodyContent.trim() !== ''
    );
  };

  // Block sending the current contact while its AI image is still generating,
  // so no mail goes out with an unfinished/placeholder image.
  const isGeneratingImages = !!(
    currentBulkContact &&
    Object.values(bulkAiImages[currentBulkContact.id] || {}).some((v) => v && v.status === "loading")
  );

  // 🔥 NEUE FUNKTION: AI Tag basierend auf Topic nach E-Mail-Versand setzen
  const setAiTagAfterEmailSend = async (topic, contactId) => {
    try {
      console.log('🏷️ [BULK-POST-EMAIL-TAG] Checking if topic has associated AI Tag:', topic);
      
      if (!userId || !contactId || !topic) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Missing required parameters');
        return false;
      }
      
      // Erweiterte Suche: Prüfe sowohl in selectedTopics als auch in customTopics
      let matchingTopic = null;
      
      // 1. Suche in allen userTopics (sowohl selectedTopics als auch customTopics)
      matchingTopic = Object.values(userTopics).find(topicData => {
        return topicData.topic === topic || topicData.type === topic;
      });
      
      // 2. Falls nicht gefunden, erweiterte Suche in der Struktur
      if (!matchingTopic && userTopics.selectedTopics) {
        matchingTopic = Object.values(userTopics.selectedTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      // 3. Falls immer noch nicht gefunden, suche in customTopics
      if (!matchingTopic && userTopics.customTopics) {
        matchingTopic = Object.values(userTopics.customTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      if (!matchingTopic) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] No matching topic found in userTopics for:', topic);
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Available topics:', {
          allTopics: Object.values(userTopics).map(t => ({ topic: t.topic, type: t.type, hasAiTagId: !!t.aiTagId })),
          selectedTopics: userTopics.selectedTopics ? Object.keys(userTopics.selectedTopics) : 'none',
          customTopics: userTopics.customTopics ? Object.keys(userTopics.customTopics) : 'none'
        });
        return false;
      }
      
      console.log('🏷️ [BULK-POST-EMAIL-TAG] Found matching topic:', matchingTopic);
      
      // Prüfe, ob das Topic einen zugeordneten AI Tag hat
      if (!matchingTopic.aiTagId) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Topic has no associated AI tag - this is normal');
        return false;
      }
      
      console.log('🏷️ [BULK-POST-EMAIL-TAG] ✅ Topic has associated AI tag ID:', matchingTopic.aiTagId);
      
      // Lade die AI Tag-Einstellungen
      const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${matchingTopic.aiTagId}`);
      const aiTagSnapshot = await new Promise((resolve) => {
        onValue(aiTagRef, resolve, { onlyOnce: true });
      });
      
      if (!aiTagSnapshot.exists()) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] AI tag not found for ID:', matchingTopic.aiTagId);
        return false;
      }
      
      const aiTagData = aiTagSnapshot.val();
      console.log('🏷️ [BULK-POST-EMAIL-TAG] AI tag data loaded:', aiTagData);
      
      // Erstelle den neuen Tag für den Kontakt
      const newTag = {
        tag: aiTagData.tag,
        name: aiTagData.name,
        color: aiTagData.color,
        icon: aiTagData.icon,
        appliedAt: new Date().toISOString(),
        appliedBy: 'post_email_send',
        appliedTopic: topic,
        ...(aiTagData.selectedTopic && { selectedTopic: aiTagData.selectedTopic })
      };
      
      // Referenz auf das Kontakt-Dokument in Firestore
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);

      // Prüfe, ob es ein Overwrite-Tag ist
      if (aiTagData.overwriteOtherTags === true) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Overwrite tag detected, removing all other tags');
        
        // Überschreibe alle Tags mit nur diesem einen Tag
        await updateDoc(contactDocRef, { tags: { [matchingTopic.aiTagId]: newTag } });
      } else {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Adding tag alongside existing tags');
        const contactSnap = await getDoc(contactDocRef);
        const existingTags = contactSnap.exists() ? contactSnap.data().tags || {} : {};
        await updateDoc(contactDocRef, { [`tags.${matchingTopic.aiTagId}`]: newTag, tags: { ...existingTags, [matchingTopic.aiTagId]: newTag } });
      }
      
      console.log('🏷️ [BULK-POST-EMAIL-TAG] ✅ AI tag successfully applied after email send');
      
      // 🔥 Informiere die Tabelle über die Tag-Änderung für sofortige UI-Aktualisierung
      if (onEmailResult) {
        onEmailResult('tag-update', `AI tag applied to contact`, { 
          id: contactId, 
          tagId: matchingTopic.aiTagId,
          tagData: newTag,
          _optimistic_update: true,
          _prevent_toast: true
        });
      }
      
      return true;
      
    } catch (error) {
      console.error('🏷️ [BULK-POST-EMAIL-TAG] Error setting AI tag after email send:', error);
      return false;
    }
  };

  // CC-Status für eine E-Mail umschalten
  const toggleCcEmail = (email) => {
    if (ccEmails.includes(email)) {
      setCcEmails(ccEmails.filter(e => e !== email));
    } else {
      if (email !== selectedEmail) {
        setCcEmails([...ccEmails, email]);
      }
    }
  };

  const handleVerifyDmEmail = async (entry) => {
    if (!entry || entry.source !== 'decision_maker' || !entry.email || !currentBulkContact?.id) return;
    const emailKey = entry.email.toLowerCase();
    if (checkingDmEmails[emailKey]) return;
    setCheckingDmEmails(prev => ({ ...prev, [emailKey]: true }));
    try {
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.validateVerifyEmail(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: entry.email }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const resultStatus = data?.result?.status;
      const isServiceError = ['service_error', 'unknown', 'antispam_system', 'smtp_protocol'].includes(resultStatus);
      if (!data?.success || isServiceError) {
        console.warn('[DM-EMAIL-CHECK] Inconclusive result, keeping email as-is:', resultStatus);
        setDmEmailOverrides(prev => ({ ...prev, [emailKey]: { checked: true, inconclusive: true } }));
        return;
      }
      const isStrictValid = data.result.isValid === true && resultStatus === 'ok';
      const isCatchAll = data.result.isCatchAll === true || resultStatus === 'ok_for_all';

      const raw = currentBulkContact.lead_decision_makers;
      const dms = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(dms)) throw new Error('Invalid decision makers payload');

      const dmIndex = dms.findIndex((dm) => (dm?.email || '').toLowerCase() === emailKey);
      if (dmIndex === -1) throw new Error('Decision maker not found');

      const updatedDms = [...dms];
      if (isStrictValid) {
        updatedDms[dmIndex] = {
          ...updatedDms[dmIndex],
          emailChecked: true,
          emailVerified: true,
          emailValidationFailed: false,
          emailValidationStatus: 'valid',
        };
      } else if (isCatchAll) {
        updatedDms[dmIndex] = {
          ...updatedDms[dmIndex],
          emailChecked: true,
          emailVerified: false,
          emailValidationFailed: false,
          emailValidationStatus: 'catch_all',
          emailNote: 'catch-all domain — unverified, may bounce',
        };
      } else {
        updatedDms[dmIndex] = {
          ...updatedDms[dmIndex],
          email: null,
          emailChecked: true,
          emailVerified: false,
          emailValidationFailed: true,
          emailValidationStatus: 'invalid',
          emailSearched: true,
        };
      }

      const dmJson = JSON.stringify(updatedDms);
      const contactRef = doc(firestore, `World/${userId}/db/${currentBulkContact.id}`);
      await updateDoc(contactRef, { lead_decision_makers: dmJson });

      setCurrentBulkContact(prev => prev ? { ...prev, lead_decision_makers: dmJson } : prev);

      const wasRemoved = !isStrictValid && !isCatchAll;
      if (wasRemoved && selectedEmail && selectedEmail.toLowerCase() === emailKey) {
        const remaining = getAllContactEmails({ ...currentBulkContact, lead_decision_makers: dmJson });
        const fallback = remaining.find((e) => e.email && e.email.toLowerCase() !== emailKey);
        setSelectedEmail(fallback ? fallback.email : remaining[0]?.email || '');
      }
    } catch (error) {
      console.error('[DM-EMAIL-CHECK] Failed to validate DM email:', error);
    } finally {
      setCheckingDmEmails(prev => ({ ...prev, [emailKey]: false }));
    }
  };

  const handleSaveInlineDm = async () => {
    if (!inlineAddDm || !currentBulkContact?.id || savingDm) return;
    const name = (inlineAddDm.name || '').trim();
    const email = (inlineAddDm.email || '').trim();
    const role = (inlineAddDm.role || '').trim();
    if (!name && !email) return;
    setSavingDm(true);
    try {
      const raw = currentBulkContact.lead_decision_makers;
      let dms = [];
      try {
        if (raw && typeof raw === 'string') dms = JSON.parse(raw);
        else if (Array.isArray(raw)) dms = raw;
      } catch (_) {}
      dms.push({ name, email, role });
      const dmJson = JSON.stringify(dms);
      const contactRef = doc(firestore, `World/${userId}/db/${currentBulkContact.id}`);
      await updateDoc(contactRef, { lead_decision_makers: dmJson });
      setCurrentBulkContact(prev => prev ? { ...prev, lead_decision_makers: dmJson } : prev);
      setInlineAddDm(null);
      if (email) setSelectedEmail(email);
    } catch (error) {
      console.error('[ADD-DM] Failed to save decision maker:', error);
    } finally {
      setSavingDm(false);
    }
  };

  const currentContactAllEmails = currentBulkContact ? getAllContactEmails(currentBulkContact) : [];
  const currentContactEmails = currentContactAllEmails.filter(e => !e.emailMissing).map(e => e.email);

  // Addresses that hard-bounced → undeliverable (struck through, not selectable,
  // send-blocked). Driven primarily by the live undeliverableEmails index (the
  // same source the server-side send gates enforce) so it is robust regardless
  // of whether the bounce matched this contact; the contact's emailBounceCheck
  // is a fallback. Per-address only — no blacklist / no opt-out.
  const { undeliverableSet, bounceTypeOf } = useUndeliverableEmails(userId);
  const isEmailUndeliverable = (email) => {
    if (!email) return false;
    const lower = String(email).toLowerCase();
    if (undeliverableSet.has(lower)) return true;
    const bc = currentBulkContact?.emailBounceCheck;
    return !!(bc && bc.action === 'undeliverable' && bc.originalEmail &&
      String(bc.originalEmail).toLowerCase() === lower);
  };

  // Addresses already mailed (intro/follow-up) for the current bulk contact →
  // flagged red in the To-field and gated behind an extra send confirmation.
  const { isContacted: isAddressContacted, sentCount, lastSent, sentMessages, priorSends, loading: sentHistoryLoading } = useSentRecipients(userId, currentBulkContact?.id, currentContactEmails);

  // A previously-contacted address is only a "duplicate send" to guard against
  // when composing a first-touch mail (intro). In a follow-up step, mailing an
  // address we already introduced ourselves to is the entire point — so we must
  // NOT flag it red or gate it behind the resend confirm there.
  const isResendBlocked = (email) => !isFollowUpStep && isAddressContacted(email);

  // Follow-up warning is step-specific. Intro already sent + first follow-up
  // is the normal path and stays quiet. The same stage (follow-up 1, 2 or 3)
  // or the same template sent again to this address is what gets confirmed.
  const followUpDuplicates = (isFollowUpStep && !sentHistoryLoading)
    ? duplicateFollowUpSends(priorSends, {
        email: selectedEmail,
        stage: topicTypeMapping?.[selectedTopic],
        topicName: selectedTopic,
        templateId: bulkGeneratedEmails[currentBulkContact?.id]?._trackingTemplateId || null,
      })
    : [];

  useEffect(() => {
    setCheckingDmEmails({});
    setDmEmailOverrides({});
    setInlineAddDm(null);
  }, [currentBulkContact?.id]);

  const handleTopicChange = (topic) => {
    console.log('📧 [BULK-TOPIC-CHANGE] Manual topic change from', selectedTopic, 'to', topic);
    
    if (currentBulkContact?.id) manualTopicForRef.current.add(currentBulkContact.id);
    setSelectedTopic(topic);
    
    if (currentBulkContact && !userEditedSubject) {
      updateSubjectInputDirectly(getTopicSubjectLine(currentBulkContact, topic));
    }
  };

  // Calendly-Link öffnen
  const handleOpenCalendly = () => {
    if (userSettings.calendlyBookingLink) {
      window.open(userSettings.calendlyBookingLink, '_blank');
    }
    setIsActionMenuOpen(false); // Menu nach Aktion schließen
  };

  // Calendly-Link kopieren
  const handleCopyCalendlyLink = async () => {
    if (userSettings.calendlyBookingLink) {
      try {
        await navigator.clipboard.writeText(userSettings.calendlyBookingLink);
        // Optionally show a success message
      } catch (err) {
        console.error('Failed to copy Calendly link:', err);
      }
    }
    setIsActionMenuOpen(false); // Menu nach Aktion schließen
  };

  // Action Menu togglen
  const toggleActionMenu = () => {
    setIsActionMenuOpen(!isActionMenuOpen);
  };

  // Action ausführen und Menu schließen
  const handlePerfectAndClose = () => {
    setPerfectSliderOpen(true);
    setIsActionMenuOpen(false);
  };

  const handleRegenerateAndClose = () => {
    const note = regenCustomNote.trim();
    handleRegenerateForCurrentContact(undefined, note || undefined);
    setRegenCustomNote('');
    setIsActionMenuOpen(false);
  };

  const handleFrameworkRegenerate = (personalityId, frameworkId) => {
    const personality = emailPersonalities.find(p => p.id === personalityId);
    if (!personality) return;
    const framework = personality.frameworks.find(f => f.id === frameworkId);
    if (!framework) return;
    const prompt = `${personality.corePhilosophy}\n\n${framework.prompt}`;
    const note = regenCustomNote.trim();
    setFrameworkPickerOpen(false);
    setRegenCustomNote('');
    setIsActionMenuOpen(false);
    handleRegenerateForCurrentContact(prompt, note || undefined);
  };

  // Re-Generate the current contact's email with an alternative trained
  // CopyTwin — the template's saved clone stays untouched.
  const handleCloneRegenerate = (cloneId) => {
    const note = regenCustomNote.trim();
    setFrameworkPickerOpen(false);
    setRegenCustomNote('');
    setIsActionMenuOpen(false);
    handleRegenerateForCurrentContact(undefined, note || undefined, cloneId || undefined);
  };

  // Standalone quick Re-Generate (next to the 3-dots menu) — one tap to open,
  // one tap on a CopyTwin to generate. No custom note, just a fast switch.
  const handleQuickCloneRegenerate = (cloneId) => {
    setCloneQuickOpen(false);
    handleRegenerateForCurrentContact(undefined, undefined, cloneId || undefined);
  };

  // Click outside to close the quick CopyTwin Re-Generate popover.
  useEffect(() => {
    if (!cloneQuickOpen) return undefined;
    const handleClickOutside = (event) => {
      if (regenQuickRef.current && !regenQuickRef.current.contains(event.target)) {
        setCloneQuickOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cloneQuickOpen]);

  // Click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setIsActionMenuOpen(false);
        setFrameworkPickerOpen(false);
        setExpandedPersonality(null);
      }
    };

    if (isActionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActionMenuOpen]);

  return (
    <div className={`emailAiPopupShell ${sidePanelOpen ? 'side-panel-open' : ''} ${(currentBulkContact || rowData)?.tags?.favorite ? 'is-favorite-popup' : ''}`}>
    <div className="emailAiPopupContainer bulk-mode">
      <div className="emailAiPopupHeader">
        <h2>AI E-Mail Manager (Bulk-Modus)</h2>
        <button className="emailAiPopupCloseButton" onClick={handleClose}>×</button>
      </div>
      
      <BulkModeProgress 
        currentBulkIndex={currentBulkIndex}
        totalContacts={updatedBulkContacts.length}
        goToPreviousBulkContact={goToPreviousBulkContact}
        goToNextBulkContact={goToNextBulkContact}
        currentBulkContact={currentBulkContact}
        sentEmails={sentEmails}
        canGoToPrevious={canGoToPrevious}
        canGoToNext={canGoToNext}
        bulkContacts={updatedBulkContacts}
        onDeleteContact={handleDeleteContact}
      />
      
      {creditError && (
        <div className="bulk-credit-warning-banner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>
            {creditError.exhausted
              ? 'Not enough credits — email generation stopped.'
              : `Low credits: ${creditError.available} available, ~${creditError.needed} needed for ${creditError.total} emails. Only ${creditError.affordable} emails can be generated.`}
          </span>
          <button
            type="button"
            className="bulk-credit-warning-banner__cta"
            onClick={() => openCreditsModal()}
          >
            Get more credits
          </button>
          <button onClick={() => setCreditError(null)}>×</button>
        </div>
      )}

      <div className="emailAiPopupContent">
        {/* LINKE SPALTE: Topics, Style, Context */}
        <div className="emailAiPopupLeftColumn">
          <div className="emailAiPopupTopicSection">
            <div className="topicCircleRow">
              <div className="topicCircleConnectingLine" />
              {[
                { label: "Introduction", internal: "Introduce Yourself" },
                { label: "Follow Up 1", internal: "Follow Up" },
                { label: "Follow Up 2", internal: "Second Follow Up" },
                { label: "Follow Up 3", internal: "Third Follow Up" }
              ].map((topic, index) => {
                const isActive = selectedTopic === topic.internal;
                return (
                  <button
                    key={topic.internal}
                    className={`topicCircleItem ${isActive ? "active" : ""}`}
                    onClick={() => handleTopicChange(topic.internal)}
                  >
                    <span className="topicCircleNumber">{index + 1}</span>
                    <span className="topicCircleLabel">{topic.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="emailAiPopupContextSection">
            <div className="bcDescTopBar">
              <h3 className="emailAiPopupSectionTitle">Description</h3>
              {/* Social Media Buttons — moved to the top of the container */}
              {currentBulkContact && (
                <div className="bonusclub-social-buttons-container">
                  {currentBulkContact.lead_website && (
                    <a 
                      href={formatURL(currentBulkContact.lead_website, 'https://')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-website"
                      title="Website besuchen"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                      </svg>
                    </a>
                  )}
                  {currentBulkContact.lead_instagram && (
                    <a 
                      href={formatURL(currentBulkContact.lead_instagram, 'https://instagram.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-instagram"
                      title="Instagram Profil"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    </a>
                  )}
                  {currentBulkContact.lead_facebook && (
                    <a 
                      href={formatURL(currentBulkContact.lead_facebook, 'https://facebook.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-facebook"
                      title="Facebook Seite"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                      </svg>
                    </a>
                  )}
                  {currentBulkContact.lead_linkedin && (
                    <a 
                      href={formatURL(currentBulkContact.lead_linkedin, 'https://linkedin.com/in/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-linkedin"
                      title="LinkedIn Profil"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect x="2" y="9" width="4" height="12"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
            {currentBulkContact && (() => {
              const leadFields = [
                { label: "Company", value: currentBulkContact.business_name, icon: "fa fa-building" },
                { label: "City", value: currentBulkContact.lead_city, icon: "fa fa-location-dot" },
              ].filter((f) => f.value);
              if (leadFields.length === 0) return null;
              return (
                <div className="bcLeadFields">
                  {leadFields.map((f) => (
                    <div key={f.label} className="bcLeadField" title={`${f.label}: ${f.value}`}>
                      <span className="bcLeadFieldIcon"><i className={f.icon} /></span>
                      <div className="bcLeadFieldText">
                        <span className="bcLeadFieldLabel">{f.label}</span>
                        <span className="bcLeadFieldValue">{f.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="emailAiPopupContextWrapper">
              <textarea
                className="emailAiPopupContextInput"
                placeholder="Kontext wird für jeden Kontakt automatisch geladen..."
                value={contextInfo}
                onChange={(e) => setContextInfo(e.target.value)}
              ></textarea>
            </div>
          </div>
          <ResearchInfoTabs contact={currentBulkContact} />
        </div>

        {/* RECHTE SPALTE: Subject, Body, Extra Buttons, Generate/Send */}
        <div className="emailAiPopupRight">
          {/* Absender-E-Mail-Auswahl */}
          <div className="emailAiPopupBottomButtons">
            <div className="emailAiPopupActionButtons" ref={actionMenuRef}>
              <div className="cloneQuickWrap" ref={regenQuickRef}>
                <button
                  type="button"
                  className={`cloneQuickButton ${cloneQuickOpen ? 'active' : ''}`}
                  onClick={() => setCloneQuickOpen(o => !o)}
                  disabled={isGenerating || isBulkGenerating || isPerfecting}
                  title="Re-Generate"
                >
                  <i className="fa-solid fa-arrows-rotate" />
                </button>
                {cloneQuickOpen && (
                  <div className={`cloneQuickPopover ${cloneRegenState.isCloneRegen && cloneRegenState.switchableClones.length > 0 ? 'isClones' : 'isStandard'}`}>
                    {cloneRegenState.isCloneRegen && cloneRegenState.switchableClones.length > 0 ? (
                      <>
                        <div className="cloneQuickTitle">
                          <i className="fa-solid fa-wand-magic-sparkles" />
                          <span>Re-Generate with CopyTwin</span>
                        </div>
                        <div className="cloneQuickRow">
                          {cloneRegenState.switchableClones.map(c => {
                            const isDefault = c.id === cloneRegenState.defaultCloneId;
                            const label = cloneRegenLabel(c);
                            const initial = (label.match(/[A-Za-z0-9]/) || ['•'])[0].toUpperCase();
                            return (
                              <button
                                key={c.id}
                                type="button"
                                className={`cloneQuickItem ${isDefault ? 'isDefault' : ''}`}
                                onClick={() => handleQuickCloneRegenerate(isDefault ? null : c.id)}
                                title={isDefault ? `${label} (template default)` : `Re-Generate with ${label}`}
                              >
                                <span className="cloneQuickAvatar">
                                  {c.avatar ? <img src={c.avatar} alt="" /> : <span>{initial}</span>}
                                </span>
                                <span className="cloneQuickName">{label}</span>
                                {isDefault && <span className="cloneQuickDot" />}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="cloneQuickStandard">
                        <div className="framework-picker-custom-note">
                          <textarea
                            className="framework-picker-custom-textarea"
                            placeholder="Spezielle Anweisungen (optional)..."
                            value={regenCustomNote}
                            onChange={(e) => setRegenCustomNote(e.target.value)}
                            rows={2}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        <button
                          className="framework-picker-standard"
                          onClick={() => { setCloneQuickOpen(false); handleRegenerateAndClose(); }}
                        >
                          <i className="fa-solid fa-refresh" />
                          <span>Standard Re-Generate</span>
                        </button>
                        <div className="framework-picker-divider" />
                        {emailPersonalities.map(p => (
                          <div key={p.id} className="framework-picker-personality">
                            <button
                              className={`framework-picker-personality-btn ${expandedPersonality === p.id ? 'active' : ''}`}
                              onClick={() => setExpandedPersonality(expandedPersonality === p.id ? null : p.id)}
                            >
                              <img src={p.image} alt={p.name} className="framework-picker-avatar" />
                              <div className="framework-picker-personality-info">
                                <span className="framework-picker-personality-name">{p.name}</span>
                                <span className="framework-picker-personality-tagline">{p.tagline}</span>
                              </div>
                              <i className={`fa-solid fa-chevron-${expandedPersonality === p.id ? 'up' : 'down'}`} style={{fontSize: '9px', color: 'var(--blue, #6897B7)', opacity: 0.5}} />
                            </button>
                            {expandedPersonality === p.id && (
                              <div className="framework-picker-frameworks">
                                {p.frameworks.map(fw => (
                                  <button
                                    key={fw.id}
                                    className="framework-picker-fw-btn"
                                    onClick={() => { setCloneQuickOpen(false); handleFrameworkRegenerate(p.id, fw.id); }}
                                  >
                                    <div className="framework-picker-fw-icon">
                                      <i className={`fa-solid ${fw.icon}`} />
                                    </div>
                                    <div className="framework-picker-fw-info">
                                      <span className="framework-picker-fw-name">{fw.name}</span>
                                      <span className="framework-picker-fw-short">{fw.short}</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button 
                className={`emailAiPopupActionMenuButton ${isActionMenuOpen ? 'active' : ''}`}
                onClick={toggleActionMenu}
                disabled={isGenerating || isBulkGenerating || isPerfecting}
                title="AI Actions"
              >
                <img src="/img/menu_1.svg" alt="AI Actions" className="emailAiPopupActionMenuIcon" />
              </button>
              
                              {isActionMenuOpen && (
                  <div className="emailAiPopupActionMenu">
                    {userSettings.calendlyBookingLink && (
                      <button
                        className="emailAiPopupActionMenuItem"
                        onClick={handleCopyCalendlyLink}
                      >
                        <div className="emailAiPopupActionMenuItemIconWrapper">
                          <img src="/img/copy.svg" alt="Copy Link" className="emailAiPopupActionMenuItemIcon" />
                        </div>
                        <span>Copy Calendly Link</span>
                      </button>
                    )}
                    {userSettings.calendlyBookingLink && (
                      <button
                        className="emailAiPopupActionMenuItem"
                        onClick={handleOpenCalendly}
                      >
                        <div className="emailAiPopupActionMenuItemIconWrapper">
                          <img src="/img/calendly.svg" alt="Calendly" className="emailAiPopupActionMenuItemIcon" />
                        </div>
                        <span>Open Calendly</span>
                      </button>
                    )}
                    <button
                      className="emailAiPopupActionMenuItem"
                      onClick={handlePerfectAndClose}
                      disabled={!bodyContent || bodyContent.trim() === ""}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <img src="/img/magic-wand.svg" alt="Perfect" className="emailAiPopupActionMenuItemIcon" />
                      </div>
                      <span>Make Perfect <span className="aiLabel">AI</span></span>
                    </button>
                    <button
                      className="emailAiPopupActionMenuItem"
                      onClick={() => { setFrameworkPickerOpen(!frameworkPickerOpen); }}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <img src="/img/refresh-arrow.svg" alt="Regenerate" className="emailAiPopupActionMenuItemIcon" />
                      </div>
                      <span>Re-Generate <span className="aiLabel">AI</span></span>
                      <i className={`fa-solid fa-chevron-${frameworkPickerOpen ? 'up' : 'right'}`} style={{marginLeft: 'auto', fontSize: '9px', color: 'var(--blue, #6897B7)', opacity: 0.5}} />
                    </button>
                    {frameworkPickerOpen && (() => {
                      // CopyTwin template? Offer a one-off clone switch instead
                      // of the standard writer personas (which don't apply to
                      // voice-clone generation).
                      const { isCloneRegen, defaultCloneId, switchableClones } = cloneRegenState;
                      const cloneLabel = cloneRegenLabel;
                      // Hide the divider when nothing follows it (CopyTwin with a single clone).
                      const showAfterDivider = isCloneRegen ? switchableClones.length > 1 : true;
                      return (
                      <div className="framework-picker-panel">
                        <div className="framework-picker-custom-note">
                          <textarea
                            className="framework-picker-custom-textarea"
                            placeholder="Spezielle Anweisungen (optional)..."
                            value={regenCustomNote}
                            onChange={(e) => setRegenCustomNote(e.target.value)}
                            rows={2}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        <button 
                          className="framework-picker-standard"
                          onClick={handleRegenerateAndClose}
                        >
                          <i className="fa-solid fa-refresh" />
                          <span>Standard Re-Generate</span>
                        </button>
                        {showAfterDivider && <div className="framework-picker-divider" />}
                        {isCloneRegen ? (
                          switchableClones.length > 1 ? (
                            <div className="cloneSwitch">
                              <div className="cloneSwitchLabel">
                                <i className="fa-solid fa-wand-magic-sparkles" />
                                <span>Generate with another CopyTwin</span>
                              </div>
                              <div className="cloneSwitchList">
                                {switchableClones.map(c => {
                                  const isDefault = c.id === defaultCloneId;
                                  const label = cloneLabel(c);
                                  const initial = (label.match(/[A-Za-z0-9]/) || ['•'])[0].toUpperCase();
                                  return (
                                    <button
                                      key={c.id}
                                      className={`cloneSwitchItem ${isDefault ? 'isDefault' : ''}`}
                                      onClick={() => handleCloneRegenerate(isDefault ? null : c.id)}
                                      title={isDefault ? `${label} (template default)` : `Re-Generate with ${label}`}
                                    >
                                      <span className="cloneSwitchAvatar">
                                        {c.avatar
                                          ? <img src={c.avatar} alt="" />
                                          : <span>{initial}</span>}
                                      </span>
                                      <span className="cloneSwitchName">{label}</span>
                                      {isDefault
                                        ? <span className="cloneSwitchBadge">Default</span>
                                        : <i className="fa-solid fa-arrow-right cloneSwitchGo" />}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null
                        ) : (
                          emailPersonalities.map(p => (
                          <div key={p.id} className="framework-picker-personality">
                            <button 
                              className={`framework-picker-personality-btn ${expandedPersonality === p.id ? 'active' : ''}`}
                              onClick={() => setExpandedPersonality(expandedPersonality === p.id ? null : p.id)}
                            >
                              <img src={p.image} alt={p.name} className="framework-picker-avatar" />
                              <div className="framework-picker-personality-info">
                                <span className="framework-picker-personality-name">{p.name}</span>
                                <span className="framework-picker-personality-tagline">{p.tagline}</span>
                              </div>
                              <i className={`fa-solid fa-chevron-${expandedPersonality === p.id ? 'up' : 'down'}`} style={{fontSize: '9px', color: 'var(--blue, #6897B7)', opacity: 0.5}} />
                            </button>
                            {expandedPersonality === p.id && (
                              <div className="framework-picker-frameworks">
                                {p.frameworks.map(fw => (
                                  <button 
                                    key={fw.id}
                                    className="framework-picker-fw-btn"
                                    onClick={() => handleFrameworkRegenerate(p.id, fw.id)}
                                  >
                                    <div className="framework-picker-fw-icon">
                                      <i className={`fa-solid ${fw.icon}`} />
                                    </div>
                                    <div className="framework-picker-fw-info">
                                      <span className="framework-picker-fw-name">{fw.name}</span>
                                      <span className="framework-picker-fw-short">{fw.short}</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          ))
                        )}
                      </div>
                      );
                    })()}
                    <div style={{height: '1px', background: '#e5e7eb', margin: '4px 8px'}}></div>
                    <button
                      className="emailAiPopupActionMenuItem"
                      onClick={() => { setShowScreenComposer(true); setIsActionMenuOpen(false); }}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <rect x="2" y="3" width="20" height="14" rx="2"/>
                          <path d="M8 21H16M12 17V21"/>
                        </svg>
                      </div>
                      <span>Add Image/Gif</span>
                    </button>
                  </div>
                )}
              <EmailHistoryPanel
                userId={userId}
                contactId={currentBulkContact?.id}
                recipientEmails={currentContactEmails}
              />
            </div>
            <ScreenComposerNotifier
              isGenerating={scBackground.isGenerating}
              progress={scBackground.progress}
              error={scBackground.error}
              justCompleted={scBackground.justCompleted}
              onDismiss={scBackground.dismiss}
              onCancel={scBackground.cancel}
            />
            {followUpDuplicates.length > 0 && (
              <FollowUpHistoryWarning
                sentCount={followUpDuplicates.length}
                lastSent={followUpDuplicates[0]}
              />
            )}
            <CallContextChip contact={currentBulkContact} topic={selectedTopicData} />
            <LiveSpamBadge
              body={bodyContent}
              subject={subjectLine}
              mode={spamCheckMode}
              aiResult={aiSpam}
              aiPending={aiSpamPending}
              isFollowUp={isFollowUpStep}
            />
            <button
              className={`emailAiPopupSendButton ${sendSuccess ? 'success' : ''} ${isSending ? 'sending' : ''} ${!isEmailValid() || isGeneratingImages ? 'disabled' : ''}`}
              onClick={handleSend}
              disabled={isGenerating || isSending || !isEmailValid() || isBulkGenerating || isPerfecting || isGeneratingImages || (currentBulkContact && isContactSent(currentBulkContact.id))}
              title={isGeneratingImages ? "Waiting for the AI image to finish generating…" : undefined}
            >
              {isSending ? (
                <>
                  <div className="emailAiPopupSpinner"></div>
                  <span>Sending...</span>
                </>
              ) : isGeneratingImages ? (
                <>
                  <div className="emailAiPopupSpinner"></div>
                  <span>Generating image…</span>
                </>
              ) : sendSuccess ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Sent</span>
                </>
              ) : (
                <>
                  <span>{currentBulkIndex === updatedBulkContacts.length - 1 ? 'Send & Finish' : 'Send & Next'}</span>
                  <img src="/img/sender.svg" alt="Send" className="emailAiPopupSendIcon" />
                </>
              )}
            </button>
          </div>
          <div className="emailAiPopupField">
            <h3 className="emailAiPopupSectionTitle">From</h3>
            <div className="emailAiPopupSenderEmailsContainer">
              {senderEmails.length > 0 ? (
                <div className="emailAiPopupSenderEmailsScroll">
                  {senderEmails.length > 1 && !currentPreparedAccount && (
                    <button
                      className={`emailAiPopupSenderEmailButton emailAiPopupSenderRandomBtn ${randomSenderMode ? 'active' : ''}`}
                      onClick={() => {
                        const next = !randomSenderMode;
                        setRandomSenderMode(next);
                        if (next) {
                          const others = senderEmails.filter(e => e.email !== selectedSenderEmail);
                          const pick = others[Math.floor(Math.random() * others.length)];
                          setSelectedSenderEmail(pick.email);
                        }
                      }}
                      title={randomSenderMode ? "Random sender active — click to disable" : "Enable random sender rotation"}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 3h5v5" /><path d="M4 20 21 3" />
                        <path d="M21 16v5h-5" /><path d="M15 15l6 6" />
                        <path d="M4 4l5 5" />
                      </svg>
                      Random
                    </button>
                  )}
                  {senderEmails.map((item) => (
                    <button 
                      key={item.email}
                      className={`emailAiPopupSenderEmailButton ${!randomSenderMode && selectedSenderEmail === item.email ? 'active' : ''}`}
                      onClick={() => {
                        setRandomSenderMode(false);
                        setSelectedSenderEmail(item.email);
                      }}
                    >
                      {item.sender_name
                        ? <><span style={{fontWeight: 500}}>{item.sender_name}</span><span style={{marginLeft: 8, opacity: 0.6}}>{item.email}</span></>
                        : item.email}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="emailAiPopupNoEmails">No sender emails configured.</p>
              )}
            </div>
          </div>

          {/* Empfänger-E-Mail-Anzeige */}
          <div className="emailAiPopupRecipientSection">
            <h3 className="emailAiPopupSectionTitle">To</h3>
            <div className="emailAiPopupTopButtons">
              {currentContactAllEmails.map((entry, index) => {
                const undeliverable = isEmailUndeliverable(entry.email);
                const undeliverableType = undeliverable ? (bounceTypeOf(entry.email) || 'hard') : null;
                return (
                <div key={index} className="emailAiPopupButtonWrapper">
                  {entry.emailMissing ? (
                    <button
                      className="emailAiPopupButton dm-email-missing"
                      disabled
                      title={`Decision Maker: ${entry.dmName} – Email not found`}
                    >
                      <span style={{fontWeight: 600}}>{entry.dmName}</span>
                      {entry.dmRole && <span className="dm-role-badge">{entry.dmRole}</span>}
                      <span className="dm-email-missing-label">No email</span>
                    </button>
                  ) : (
                  <button
                    className={`emailAiPopupButton ${entry.email === selectedEmail ? 'active' : ''} ${isResendBlocked(entry.email) ? 'contacted' : ''} ${undeliverable ? 'undeliverable' : ''}`}
                    disabled={undeliverable}
                    onClick={() => {
                      if (undeliverable) return;
                      if (ccEmails.includes(entry.email)) {
                        setCcEmails(ccEmails.filter(e => e !== entry.email));
                      }
                      setSelectedEmail(entry.email);
                    }}
                    title={undeliverable
                      ? (undeliverableType === 'soft'
                        ? 'Undeliverable: this address soft-bounced (temporary failure) on the last send — sending blocked'
                        : 'Undeliverable: this address hard-bounced on the last send — sending blocked')
                      : (isResendBlocked(entry.email)
                        ? 'Note: an email was already sent to this address'
                        : (entry.source === 'decision_maker' ? `Decision Maker: ${entry.dmName}` : 'General email'))}
                  >
                    <span style={undeliverable ? { textDecoration: 'line-through', opacity: 0.7 } : undefined}>
                      {entry.source === 'decision_maker' && entry.dmName
                        ? <><span style={{fontWeight: 600}}>{entry.dmName}</span>{entry.dmRole && <span className="dm-role-badge">{entry.dmRole}</span>}<span style={{marginLeft: 8, opacity: 0.6}}>{entry.email}</span></>
                        : entry.email}
                    </span>
                    {undeliverable && (
                      <span className="emailAiPopupUndeliverableBadge" title={`Undeliverable — ${undeliverableType === 'soft' ? 'Soft' : 'Hard'} bounce`}>
                        <i className="fa fa-times-circle"></i>
                      </span>
                    )}
                    {isResendBlocked(entry.email) && (
                      <span className="emailAiPopupContactedBadge" title="Bereits angeschrieben">
                        <i className="fa fa-exclamation-circle"></i>
                      </span>
                    )}
                    {entry.source === 'decision_maker' && (() => {
                      const eKey = entry.email.toLowerCase();
                      const override = dmEmailOverrides[eKey];
                      const isInconclusive = override?.inconclusive;
                      const isCheckedValid = entry.emailChecked || entry.emailVerified;
                      return (
                      <span
                        className={`dm-email-check-action ${isCheckedValid ? 'checked' : ''} ${isInconclusive ? 'inconclusive' : ''} ${checkingDmEmails[eKey] ? 'loading' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleVerifyDmEmail(entry);
                        }}
                        title={isCheckedValid ? 'Decision Maker email verified' : isInconclusive ? 'Verifizierung nicht möglich – der Mailserver blockiert die Prüfung. Bei info@-Adressen ist das normal.' : 'Verify this Decision Maker email'}
                      >
                        {checkingDmEmails[eKey] ? <i className="fa fa-spinner fa-spin"></i> : <i className={`fa ${isCheckedValid ? 'fa-check-circle' : isInconclusive ? 'fa-question-circle' : 'fa-search'}`}></i>}
                      </span>
                      );
                    })()}
                    {entry.email !== selectedEmail && (
                      <span
                        className={`emailAiPopupCcInline ${ccEmails.includes(entry.email) ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleCcEmail(entry.email);
                        }}
                      >
                        {ccEmails.includes(entry.email) ? "CC ✓" : "CC"}
                      </span>
                    )}
                  </button>
                  )}
                </div>
                );
              })}
              {currentBulkContact && getDecisionMakersWithoutEmail(currentBulkContact).map((dm, i) => (
                <div key={`dm-noemail-${i}`} className="emailAiPopupButtonWrapper">
                  <span className="dm-info-badge-noemail">
                    <span style={{fontWeight: 600}}>{dm.name}</span>
                    {dm.role && <span className="dm-role-badge">{dm.role}</span>}
                  </span>
                </div>
              ))}
              {/* Inline Add Decision Maker */}
              {inlineAddDm ? (
                <div className="inline-add-dm-form">
                  <input
                    className="inline-add-dm-input"
                    placeholder="Name"
                    value={inlineAddDm.name}
                    onChange={(e) => setInlineAddDm(prev => ({ ...prev, name: e.target.value }))}
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveInlineDm()}
                  />
                  <input
                    className="inline-add-dm-input"
                    placeholder="Email"
                    type="email"
                    value={inlineAddDm.email}
                    onChange={(e) => setInlineAddDm(prev => ({ ...prev, email: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveInlineDm()}
                  />
                  <input
                    className="inline-add-dm-input inline-add-dm-role"
                    placeholder="Role"
                    value={inlineAddDm.role}
                    onChange={(e) => setInlineAddDm(prev => ({ ...prev, role: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveInlineDm()}
                  />
                  <button className="inline-add-dm-save" onClick={handleSaveInlineDm} disabled={savingDm}>
                    {savingDm ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-check"></i>}
                  </button>
                  <button className="inline-add-dm-cancel" onClick={() => setInlineAddDm(null)}>
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              ) : (
                <button
                  className="inline-add-dm-btn"
                  onClick={(e) => {
                    setInlineAddDm({ name: '', email: '', role: '' });
                    const row = e.currentTarget.closest('.emailAiPopupTopButtons');
                    if (row) requestAnimationFrame(() => { row.scrollLeft = row.scrollWidth; });
                  }}
                  title="Add Decision Maker"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              )}
            </div>
          </div>

          {/* Betreff und Inhalt */}
          <div className="subject-regen-wrapper">
            <button
              type="button"
              className={`subject-regen-btn ${subjectAltsOpen ? 'active' : ''} ${subjectAltsLoading ? 'loading' : ''}`}
              onClick={handleRegenerateSubjectLines}
              title="Generate alternatives"
            >
              {subjectAltsLoading ? (
                <span className="subject-regen-spinner" />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              )}
            </button>
            <input
              className={`emailAiPopupSubjectInput${!subjectLine && isGenerating ? ' subject-skeleton' : ''}`}
              placeholder={!subjectLine && isGenerating ? '' : 'Subject line'}
              value={subjectLine}
              onChange={(e) => {
                setSubjectLine(e.target.value);
                setUserEditedSubject(true);
              }}
              ref={subjectInputRef}
            />
            {subjectAltsOpen && (
              <div className="subject-alts-dropdown">
                {subjectAltsLoading ? (
                  <div className="subject-alts-loading">
                    <span className="subject-alts-loading-dot" />
                    <span className="subject-alts-loading-dot" />
                    <span className="subject-alts-loading-dot" />
                  </div>
                ) : subjectAlts.length > 0 ? (
                  subjectAlts.map((alt, i) => (
                    <button
                      key={i}
                      type="button"
                      className="subject-alt-item"
                      onClick={() => handleSelectSubjectAlt(alt)}
                    >
                      <span className="subject-alt-text">{alt}</span>
                      <span className="subject-alt-use">Use</span>
                    </button>
                  ))
                ) : (
                  <div className="subject-alts-empty">No alternatives generated</div>
                )}
              </div>
            )}
          </div>

      
            <div className={`emailAiPopupTexfieldwrapper ${isGenerating || isBulkGenerating || isPerfecting ? 'loading' : ''}`} style={{position: 'relative'}}>

{/* Shimmer-Container für die Loading-Animation */}
<div className="salesgod-loading-container">
  <div className="sg-blob-extra"></div>
  <div className="sg-blob-extra"></div>
  <div className="sg-blob-extra"></div>
  <div className="sg-shimmer"></div>
  <img src="/img/salesgod_dark.webp" alt="BonusClub" className="salesgod-loading-logo" />
  <div className="sg-loading-text">
    <span className="sg-loading-subtitle">{isPerfecting ? 'Perfecting your email...' : 'Creating your email...'}</span>
  </div>
</div>

          <RichEmailEditor
            className="emailAiPopupTextArea"
            value={bodyContent}
            onChange={(v) => {
              setBodyContent(v);
              setUserEditedBody(true);
            }}
            images={bulkAiImages[currentBulkContact?.id] || {}}
            assets={userSettings?.imageLabAssets || {}}
            placeholder={isBulkGenerating || isPerfecting ? "" : "Email content"}
            disabled={isBulkGenerating || isPerfecting}
            onRegenerate={regenerateBulkImage}
            onRemove={removeBulkImage}
            onRefine={refineBulkImage}
          />
        </div>
          {screenComposerImage && (
            <div className="sc-inline-preview">
              <img
                src={screenComposerImage.url}
                alt=""
                className="sc-inline-preview-img"
              />
              <button className="sc-inline-preview-remove" onClick={() => setScreenComposerImage(null)} title="Remove">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Bulk-Modus-spezifische Komponenten */}
      {isBulkGenerating && (
        <BulkGenerationLoading 
          progress={bulkGenerationProgress} 
          totalEmails={updatedBulkContacts.length}
          currentEmail={Math.ceil((bulkGenerationProgress / 100) * Math.min(5, updatedBulkContacts.length))}
          onTransitionComplete={() => setIsBulkGenerating(false)}
        />
      )}

      {/* Perfect Slider Popup */}
      {perfectSliderOpen && (
        <div className="perfect-slider-overlay" onClick={() => setPerfectSliderOpen(false)}>
          <div className="perfect-slider-popup" onClick={e => e.stopPropagation()}>
            <div className="perfect-slider-header">
              <svg className="perfect-slider-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="url(#sparkleGradB)" stroke="url(#sparkleGradB)" strokeWidth="0.5" strokeLinejoin="round"/>
                <defs><linearGradient id="sparkleGradB" x1="3" y1="2" x2="21" y2="22" gradientUnits="userSpaceOnUse"><stop stopColor="#6897B7"/><stop offset="1" stopColor="#2a6776"/></linearGradient></defs>
              </svg>
              <h3>Make Perfect</h3>
            </div>
            <p className="perfect-slider-desc">
              {perfectCreativity <= 2 ? 'Fix typos & grammar only — keep text almost identical'
                : perfectCreativity <= 4 ? 'Polish wording & improve clarity — same structure'
                : perfectCreativity <= 6 ? 'Rephrase & enhance — noticeable improvements'
                : perfectCreativity <= 8 ? 'Substantial rewrite — fresh phrasing, same intent'
                : 'Completely new email — new angle, new structure'}
            </p>
            <div className="perfect-slider-track">
              <input
                type="range"
                min="1"
                max="10"
                value={perfectCreativity}
                onChange={e => setPerfectCreativity(Number(e.target.value))}
                className="perfect-slider-input"
              />
              <div className="perfect-slider-steps">
                {[
                  { val: 1, label: 'Fix' },
                  { val: 3, label: 'Polish' },
                  { val: 5, label: 'Enhance' },
                  { val: 7, label: 'Rewrite' },
                  { val: 10, label: 'New' },
                ].map(s => (
                  <span
                    key={s.val}
                    className={`perfect-slider-step ${perfectCreativity >= s.val ? 'active' : ''}`}
                    onClick={() => setPerfectCreativity(s.val)}
                  >{s.label}</span>
                ))}
              </div>
            </div>
            <div className="perfect-slider-actions">
              <button className="perfect-slider-cancel" onClick={() => setPerfectSliderOpen(false)}>Cancel</button>
              <button className="perfect-slider-go" onClick={() => { setPerfectSliderOpen(false); handlePerfectCurrentEmail(perfectCreativity); }}>
                GO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <button className="side-panel-toggle" disabled={isGenerating || isBulkGenerating} onClick={() => setSidePanelOpen(prev => !prev)} title={sidePanelOpen ? 'Close panel' : 'Open panel'}>
      <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d={sidePanelOpen ? "M5.5 1L1 6L5.5 11" : "M1.5 1L6 6L1.5 11"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </button>
    <div className="emailAiSidePanel">
      <div className="side-panel-content">
        <div className="sparks-header">
          <span className="sparks-title">SalesGod Writer</span>
          <button className="sparks-refresh" onClick={() => { setActiveDirection(null); setActiveCategory(null); fetchWritingSparks(bodyContent); }} disabled={sparksLoading} title="Neu generieren (frische Angles)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
          </button>
        </div>
        <div className="sparks-actions-bar">
          {writerDirections.length > 0 && writerDirections.slice(0, 4).map((dir) => (
            <button
              key={dir.prompt}
              className={`sparks-direction-btn ${activeDirection === dir.prompt ? 'active' : ''} ${sparksLoading ? 'sparks-btn-loading' : ''}`}
              onClick={() => {
                setActiveDirection(dir.prompt);
                setActiveCategory(null);
                fetchWritingSparks(bodyContent, null, dir.prompt);
              }}
            >
              {dir.label}
            </button>
          ))}
          {writerDirections.length > 0 && (
            <button className="sparks-more-toggle" onClick={() => setSidePanelShowMore(p => !p)}>
              {sidePanelShowMore ? '− less' : '+ more'}
            </button>
          )}
          {sidePanelShowMore && (
            <div className="sparks-categories-row">
              {WRITER_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`sparks-category-btn ${activeCategory === cat.id ? 'active' : ''} ${sparksLoading ? 'sparks-btn-loading' : ''}`}
                  onClick={() => { setActiveCategory(cat.id); setActiveDirection(null); fetchWritingSparks(bodyContent, null, null, cat.id); }}
                  title={cat.label}
                >
                  <span className="sparks-category-icon">{WRITER_CATEGORY_ICONS[cat.id]}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {sparksCopied === '__expand_failed__' && (
          <div className="sparks-expand-error">Expand fehlgeschlagen — nochmal versuchen</div>
        )}
        {sparksLoading && writingSparks.length === 0 ? (
          <div className="sparks-loading">
            {[...Array(8)].map((_, i) => <div key={i} className="spark-skeleton" />)}
          </div>
        ) : writingSparks.length > 0 ? (
          <div className="sparks-list">
            {sparksLoadingPos === 'top' && (
              <div className="sparks-inline-loading">
                {[...Array(3)].map((_, i) => <div key={`skel-top-${i}`} className="spark-skeleton spark-skeleton-inline" />)}
              </div>
            )}
            {writingSparks.map((spark, i) => {
              const sparkKey = `${i}-${spark.text?.substring(0, 20)}`;
              const prevSpark = writingSparks[i - 1];
              const isFirstPrevious = spark.previous && (i === 0 || !prevSpark?.previous);
              const isFirstIcebreaker = spark.type === 'icebreaker' && !spark.previous && (i === 0 || prevSpark?.type !== 'icebreaker' || prevSpark?.previous);
              const isFirstBatch1 = spark.batch === 1 && spark.type !== 'icebreaker' && !spark.previous && !spark.expanded && !spark.directionLabel && (i === 0 || prevSpark?.type === 'icebreaker' || prevSpark?.batch !== 1 || prevSpark?.previous);
              const isFirstBatch2 = spark.batch === 2 && !spark.previous && !spark.expanded && !spark.directionLabel && prevSpark?.batch !== 2;
              const showBatchLabel = !activeDirection && (isFirstIcebreaker || isFirstBatch1 || isFirstBatch2);
              const batchLabelText = isFirstIcebreaker ? 'Icebreaker' : (spark.batch === 2 ? 'Fresh Angles' : 'Direct Impact');
              return (
                <React.Fragment key={sparkKey}>
                  {isFirstPrevious && (
                    <div className="sparks-previous-divider">
                      <span>Vorherige Vorschläge</span>
                    </div>
                  )}
                  {showBatchLabel && (
                    <div className={`sparks-batch-label${isFirstIcebreaker ? ' sparks-batch-label-icebreaker' : ''}`}>{batchLabelText}</div>
                  )}
                  <button
                    className={`spark-card ${sparksCopied === spark.text ? 'copied' : ''} ${spark.expanded ? 'spark-expanded' : ''} ${expandingIdx === i ? 'spark-expanding' : ''} ${spark.previous ? 'spark-previous' : ''} ${spark.type === 'icebreaker' ? 'spark-icebreaker' : ''}`}
                    data-color-idx={spark.colorIdx ?? (i % 4)}
                    onClick={() => {
                      navigator.clipboard.writeText(spark.text).catch(() => {});
                      setSparksCopied(spark.text);
                      setTimeout(() => setSparksCopied(null), 1500);
                    }}
                    title="Click to copy"
                  >
                    <span className="spark-text">{spark.text}</span>
                    <span className="spark-card-footer">
                      {sparksCopied === spark.text ? (
                        <span className="spark-badge" style={{ color: '#16a34a' }}>Copied!</span>
                      ) : (() => {
                        const assignedPersona = activePersona
                          ? SALESGOD_PERSONAS.find(p => p.id === activePersona)
                          : SALESGOD_PERSONAS[i % SALESGOD_PERSONAS.length];
                        return assignedPersona ? (
                          <span
                            className={`spark-persona-tag ${activePersona === assignedPersona.id ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const newP = activePersona === assignedPersona.id ? null : assignedPersona.id;
                              setActivePersona(newP);
                              setActiveDirection(null);
                              setActiveCategory(null);
                              usedAnglesRef.current = [];
                              fetchWritingSparks(bodyContent);
                            }}
                            title={activePersona === assignedPersona.id ? 'Persona deaktivieren' : `Mit ${assignedPersona.name}'s Stil generieren`}
                          >
                            <img src={assignedPersona.image} alt="" className="spark-persona-mini" />
                            <span>{assignedPersona.name}</span>
                          </span>
                        ) : (
                          <span className="spark-badge">{spark.type === 'icebreaker' ? (spark.style || 'icebreaker') : (spark.beat || (spark.batch === 2 ? 'fresh' : 'direct'))}</span>
                        );
                      })()}
                      <span
                        className="spark-expand-btn"
                        onClick={(e) => { e.stopPropagation(); expandSpark(i); }}
                        title="Variationen generieren"
                      >
                        {expandingIdx === i ? (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="10"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></circle></svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                        )}
                      </span>
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
            {sparksLoadingPos === 'bottom' && (
              <div className="sparks-inline-loading">
                {[...Array(3)].map((_, i) => <div key={`skel-bot-${i}`} className="spark-skeleton spark-skeleton-inline" />)}
              </div>
            )}
            <button
              className="sparks-load-more-btn"
              onClick={() => fetchWritingSparks(bodyContent, null, activeDirection || undefined, activeCategory || undefined, 'bottom')}
              disabled={sparksLoading}
            >
              {sparksLoading ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="10"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></circle></svg>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  <span>Create 5 more</span>
                </>
              )}
            </button>
          </div>
        ) : (
          <p className="sparks-empty">Open the panel and hit refresh to generate sparks.</p>
        )}
      </div>
    </div>
    {showScreenComposer && (
      <ScreenComposerPopup
        onClose={() => setShowScreenComposer(false)}
        recipientUrl={currentBulkContact?.lead_website || ''}
        onInsert={(imageData) => {
          setScreenComposerImage(imageData);
          setShowScreenComposer(false);
        }}
        onGenerateBackground={(params) => {
          scBackground.startGeneration(params);
        }}
      />
    )}
    <SendConfirmModal
      isOpen={resendConfirmOpen}
      variant="danger"
      recipient={selectedEmail}
      sentCount={sentCount}
      lastSent={lastSent}
      sentMessages={sentMessages}
      title="Address already emailed"
      message={`An intro or follow-up has already been sent to ${selectedEmail}. Are you sure you want to email this exact address again?`}
      confirmText="Send anyway"
      cancelText="Cancel"
      onCancel={() => setResendConfirmOpen(false)}
      onConfirm={() => {
        setResendConfirmOpen(false);
        resendConfirmedRef.current = true;
        handleSend();
      }}
    />
    <SendConfirmModal
      isOpen={followUpConfirmOpen}
      variant="warning"
      recipient={selectedEmail}
      sentCount={followUpDuplicates.length}
      lastSent={followUpDuplicates[0] || null}
      sentMessages={followUpDuplicates}
      title="This follow-up was already sent"
      message={`This follow-up step was already sent to ${selectedEmail}. Send the same step again?`}
      confirmText="Send follow-up"
      cancelText="Cancel"
      onCancel={() => setFollowUpConfirmOpen(false)}
      onConfirm={() => {
        setFollowUpConfirmOpen(false);
        followUpConfirmedRef.current = true;
        handleSend();
      }}
    />
    </div>
  );
};

export default BulkModeEmail; 